import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  getFlight, updateFlightData, saveCrew, saveFuel, saveOil,
  addDelay, removeDelay, saveDeicing, saveSecuritySearch,
  acceptAircraft, getATAChapters, createComplaint,
  updateComplaint, getComplaints, getComplaint, getComplaintActions, complaintActionsEnabled, getMELItems, discardFlight
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import flightDataIcon from '../assets/icons/flightdata.svg';
import crewIcon from '../assets/icons/crew.svg';
import fuelUpliftIcon from '../assets/icons/fueluplift.svg';
import oilIcon from '../assets/icons/oil.svg';
import preflightIcon from '../assets/icons/preflight.svg';
import securityIcon from '../assets/icons/security.svg';
import deicingIcon from '../assets/icons/deicing.svg';
import aircraftStatusIcon from '../assets/icons/aircraftstatus.svg';
import cabinItemsIcon from '../assets/icons/cabinitems.svg';
import openComplaintsIcon from '../assets/icons/opencomplaints.svg';
import arrowLeftIcon from '../assets/icons/arrow-sm-left.svg';
import { DELAY_CODE_OPTIONS } from '../constants/delayCodes';
import AircraftStatusReport from './AircraftStatusReport';
import CommanderConfirmationModal from './CommanderConfirmationModal';
import CancelFlightModal from './CancelFlightModal';
import './OnGroundMode.css';

const TITLES = ['Captain', 'First Officer', 'Second Officer', 'Relief Pilot'];
const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FUEL_TYPES = ['Jet A1', 'Jet A'];
const VOID_ACTION_TEXT = 'Void- workorder closed due to mistake';
const CREATED_FLIGHT_PAYLOADS_KEY = 'etlb_created_flight_payloads';
const ADMIN_ROLES = new Set(['admin', 'administrator', 'super_admin', 'superadmin']);
const isAdminRole = role => {
  const value = String(role || '').trim().toLowerCase();
  return ADMIN_ROLES.has(value) || value.includes('admin');
};
const isComplaintLocked = complaint => {
  const value = complaint?.is_locked;
  return value === true || value === 1 || String(value).trim().toLowerCase() === 'true' || String(value).trim() === '1';
};

const getCreatedFlightPayload = (flightId) => {
  if (!flightId) return null;
  try {
    const cache = JSON.parse(localStorage.getItem(CREATED_FLIGHT_PAYLOADS_KEY) || '{}');
    return cache[String(flightId)] || null;
  } catch {
    return null;
  }
};
const getStatusClass = (status) => `status-${String(status || 'unknown').toLowerCase()}`;
const isTerminalComplaintStatus = (status) => ['CLOSED', 'VOID', 'VOIDED'].includes(String(status || '').toUpperCase());
const getComplaintLineClass = (complaint) => {
  const status = String(complaint?.status || '').toUpperCase();
  if (status === 'OPEN') return 'line-open';
  if (isTerminalComplaintStatus(status)) return 'line-closed';
  if (status === 'DEFERRED') {
    const dueDate = complaint?.due_at ? new Date(complaint.due_at) : null;
    if (!dueDate || Number.isNaN(dueDate.getTime())) return 'line-deferred-safe';
    const hoursRemaining = (dueDate.getTime() - Date.now()) / 3600000;
    if (hoursRemaining < 0) return 'line-deferred-overdue';
    if (hoursRemaining < 24) return 'line-deferred-warning';
    return 'line-deferred-safe';
  }
  return 'line-open';
};
const isComplaintOverdue = (complaint) => {
  if (isTerminalComplaintStatus(complaint?.status) || !complaint?.due_at) return false;
  const dueDate = new Date(complaint.due_at);
  return !Number.isNaN(dueDate.getTime()) && dueDate.getTime() < Date.now();
};
const isOpenOrOverdueComplaint = (complaint) => {
  const status = String(complaint?.status || '').toUpperCase();
  if (isTerminalComplaintStatus(status)) return false;
  return status === 'OPEN' || isComplaintOverdue(complaint);
};
const getComplaintStatusBadgeLabel = (complaint, fallback = 'OPEN') => (
  isComplaintOverdue(complaint) ? 'OVERDUE' : (complaint?.status || fallback)
);

const getComplaintStatusBadgeClass = (complaint, fallback = 'OPEN') => (
  isComplaintOverdue(complaint) ? 'status-overdue' : getStatusClass(complaint?.status || fallback)
);
const getWorkorderStatusUpdateText = (action) => {
  const actionType = String(action?.action_type || '').toUpperCase();
  const statusFrom = String(action?.status_from || '').toUpperCase();
  const statusTo = String(action?.status_to || '').toUpperCase();

  if (actionType === 'CREATED' || (!statusFrom && statusTo === 'OPEN')) return 'WORKORDER OPENED.';
  if (statusTo === 'DEFERRED' && statusFrom !== 'DEFERRED') return 'WORKORDER DEFERRED.';
  if (statusTo === 'CLOSED' && statusFrom !== 'CLOSED') return 'WORKORDER CLOSED.';
  if (actionType === 'STATUS' && action?.action_text) return action.action_text;
  return '';
};
const shouldRenderWorkorderActionCard = (action) => {
  const actionType = String(action?.action_type || '').toUpperCase();
  if (['CREATED', 'STATUS', 'CLOSE'].includes(actionType)) return false;
  return Boolean(action?.action_text || action?.metadata_json || actionType === 'VOID' || actionType === 'DISPATCH' || actionType === 'ACTION');
};
const getDispatchDeferralTypeLabel = (category) => {
  const value = String(category || '').toUpperCase();
  if (value === 'DD') return 'DD CAT';
  return 'MEL';
};
const formatMelApplicability = (value) => (
  String(value || '')
    .split(/[,;|\n]+/)
    .map(item => item.trim())
    .filter(Boolean)
);
const splitMelInstalledRequired = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return { numberInstalled: '', numberRequired: '' };

  const spacedParts = raw
    .split(/(?:\s+|\/)+/)
    .map(part => part.trim())
    .filter(Boolean);

  if (spacedParts.length >= 2) {
    return { numberInstalled: spacedParts[0], numberRequired: spacedParts[1] };
  }

  const compact = raw.replace(/[^\dA-Za-z]/g, '');
  if (!compact) return { numberInstalled: '', numberRequired: '' };
  if (!/^\d+$/.test(compact)) return { numberInstalled: compact, numberRequired: '' };
  if (compact.length === 1) return { numberInstalled: compact, numberRequired: '' };
  if (compact.length === 2) return { numberInstalled: compact.slice(0, 1), numberRequired: compact.slice(1) };
  if (compact.length === 3) return { numberInstalled: compact.slice(0, 2), numberRequired: compact.slice(2) };
  if (compact.endsWith('0')) return { numberInstalled: compact.slice(0, -1), numberRequired: compact.slice(-1) };

  const splitAt = Math.ceil(compact.length / 2);
  return { numberInstalled: compact.slice(0, splitAt), numberRequired: compact.slice(splitAt) };
};
const getMelRequirementData = (item) => {
  const fallback = splitMelInstalledRequired(item?.installed_required || item?.installedRequired);
  const placardValue = item?.placard_required ?? item?.placardRequired;
  return {
    repairInterval: item?.repair_interval || item?.mel_type || item?.melType || '-',
    numberInstalled: item?.number_installed || item?.numberInstalled || fallback.numberInstalled || '-',
    numberRequired: item?.number_required || item?.numberRequired || fallback.numberRequired || '-',
    placard: placardValue === true ? 'Yes' : placardValue === false ? 'No' : '-'
  };
};
const renderMelRequirementTable = (item) => {
  const data = getMelRequirementData(item);

  return (
    <div className="mel-requirements-table-wrap">
      <table className="mel-requirements-table">
        <thead>
          <tr>
            <th>Repair interval</th>
            <th>Nbr installed</th>
            <th>Nbr required</th>
            <th>Placard</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{data.repairInterval}</td>
            <td>{data.numberInstalled}</td>
            <td>{data.numberRequired}</td>
            <td>{data.placard}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const renderMelDetailInlineText = (text) => String(text || '');
const getMelDetailLineType = (line) => {
  const value = String(line || '').trim();
  if (/^Reference\(s\)$/i.test(value)) return 'references-title';
  if (/^\([om]\)\s+Refer to/i.test(value)) return 'reference';
  if (/^Note:/i.test(value)) return 'note';
  if (/^\d+\)\s+/.test(value)) return 'numbered';
  if (/^\([om]\)(?:\([om]\))?\s+/.test(value)) return 'condition';
  return 'body';
};
const renderMelDetailLine = (line, index) => {
  const value = String(line || '').trim();
  const type = getMelDetailLineType(value);

  if (type === 'references-title') {
    return <div key={`${index}-${value}`} className="mel-detail-line type-references-title"><span>{value}</span></div>;
  }

  const markerMatch = value.match(/^(\([om]\)(?:\([om]\))?|\d+\))\s*(.*)$/i);
  if (markerMatch) {
    return (
      <div key={`${index}-${value}`} className={`mel-detail-line type-${type}`}>
        <span className="mel-detail-marker">{markerMatch[1]}</span>
        <span>{renderMelDetailInlineText(markerMatch[2])}</span>
      </div>
    );
  }

  return (
    <div key={`${index}-${value}`} className={`mel-detail-line type-${type}`}>
      {renderMelDetailInlineText(value)}
    </div>
  );
};
const renderMelDetailText = (text) => {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  if (!lines.length) return <p>No details available.</p>;

  return (
    <div className="mel-detail-text">
      {lines.map((line, index) => renderMelDetailLine(line, index))}
    </div>
  );
};
const SIMILAR_COMPLAINT_FILTER_OPTIONS = [
  { id: 'work_order_number', label: 'Workorder / Complaint Number' },
  { id: 'section', label: 'Last Deferral Type' },
  { id: 'status', label: 'Status' },
  { id: 'mcc_reference', label: 'Temporary Workorder Number' },
  { id: 'ata_chapter', label: 'ATA Chapter' },
  { id: 'type', label: 'Type of Workorder' }
];
const CALENDAR_WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const SimilarComplaintCalendarField = ({
  fieldKey,
  label,
  value,
  isOpen,
  visibleMonth,
  onToggle,
  onChangeMonth,
  onSelectDate,
  onClear
}) => {
  const selectedDate = value ? parseISO(value) : null;
  const monthStart = startOfMonth(visibleMonth);
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(monthStart), { weekStartsOn: 1 })
  });

  return (
    <div className={`complaint-calendar-field ${fieldKey === 'to' ? 'align-right' : 'align-left'} ${isOpen ? 'open' : ''}`}>
      <button
        type="button"
        className="complaint-similar-date-trigger"
        onClick={() => onToggle(fieldKey)}
      >
        <span className={`complaint-similar-date-value ${value ? 'has-value' : ''}`}>
          {value ? format(parseISO(value), 'dd.MMM.yyyy') : label}
        </span>
        <span className={`complaint-similar-filter-chevron ${isOpen ? 'open' : ''}`}>▾</span>
      </button>

      <div className={`complaint-calendar-popover ${isOpen ? 'open' : ''}`}>
        <div className="complaint-calendar-head">
          <button type="button" className="complaint-calendar-nav" onClick={() => onChangeMonth(fieldKey, -1)}>‹</button>
          <span className="complaint-calendar-title">{format(visibleMonth, 'MMMM yyyy')}</span>
          <button type="button" className="complaint-calendar-nav" onClick={() => onChangeMonth(fieldKey, 1)}>›</button>
        </div>

        <div className="complaint-calendar-weekdays">
          {CALENDAR_WEEKDAYS.map(day => <span key={day}>{day}</span>)}
        </div>

        <div className="complaint-calendar-grid">
          {calendarDays.map(day => (
            <button
              key={day.toISOString()}
              type="button"
              className={[
                'complaint-calendar-day',
                isSameMonth(day, visibleMonth) ? 'current-month' : 'outside-month',
                selectedDate && isSameDay(day, selectedDate) ? 'selected' : ''
              ].join(' ')}
              onClick={() => onSelectDate(fieldKey, day)}
            >
              {format(day, 'd')}
            </button>
          ))}
        </div>

        <div className="complaint-calendar-footer">
          <button type="button" className="complaint-calendar-clear" onClick={() => onClear(fieldKey)}>Clear</button>
        </div>
      </div>
    </div>
  );
};

const createEmptyUplift = (number = 1) => ({
  uplift_number: number,
  departure_fuel_left: '',
  departure_fuel_center: '0',
  departure_fuel_right: '0',
  departure_fuel_other: '0',
  bowser_liters: '',
  density: '0.8',
  fuel_bill_no: '',
  fuel_type: 'Jet A1',
  fuel_unit: 'metric'
});

// Max fuel capacity by aircraft type (all tanks combined, in kg)
const MAX_FUEL_BY_TYPE = {
  'A321-271N': 18970,
  'A321-231': 18644,
  'A320-232': 19004,
  'A320-271N': 18560
};

const getMaxFuelForAircraft = (acType) => {
  if (!acType) return 20000; // Default fallback
  // Check for exact match first
  if (MAX_FUEL_BY_TYPE[acType]) return MAX_FUEL_BY_TYPE[acType];
  // Check for partial match (e.g., "A321-231" matches "A321-231")
  for (const [type, maxFuel] of Object.entries(MAX_FUEL_BY_TYPE)) {
    if (acType.includes(type) || type.includes(acType)) {
      return maxFuel;
    }
  }
  // Default based on family
  if (acType.includes('A321')) return 18644;
  if (acType.includes('A320')) return 18560;
  return 20000; // Generic fallback
};

const toFuelNumber = value => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const IMPERIAL_GALLON_TO_LITERS = 4.54609;

const formatFuelValue = value => {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const formatMinutesToHourMin = (minutes) => {
  const totalMinutes = Math.max(0, Math.round(Number(minutes) || 0));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const firstUseful = (...values) => values.find(value => value !== undefined && value !== null && String(value).trim() !== '');

const formatAircraftFlightHours = value => {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (/^\d+:\d{2}$/.test(text)) return text;

  const numericValue = Number(text);
  if (!Number.isFinite(numericValue)) return text;

  const totalMinutes = Math.max(0, Math.round(numericValue * 60));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const formatAircraftFlightCycles = value => {
  const text = String(value ?? '').trim();
  if (!text) return '';

  const numericValue = Number(text);
  return Number.isFinite(numericValue) ? String(Math.max(0, Math.round(numericValue))) : text;
};

const stripCrewIdNumbers = value => (
  String(value || '')
    .replace(/\bID\s*[:#-]?\s*\d+\b/gi, ' ')
    .replace(/\(\s*\d{2,}\s*\)/g, ' ')
    .replace(/\[\s*\d{2,}\s*\]/g, ' ')
    .replace(/([A-Za-zÀ-ÖØ-öø-ÿ])\d{2,}\b/g, '$1')
    .replace(/\b\d{2,}([A-Za-zÀ-ÖØ-öø-ÿ])/g, '$1')
    .replace(/\b\d{2,}\b/g, ' ')
    .replace(/^\s*[-|/#]+\s*/g, '')
    .replace(/\s*[-|/#]+\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
);

const getCrewNameFromUser = user => {
  const hasProfileName = String(user?.full_name || user?.name || '').trim();
  const rawName = hasProfileName || String(user?.email || '').split('@')[0].replace(/[._-]+/g, ' ');

  return stripCrewIdNumbers(rawName);
};

const getElapsedMinutes = (startValue, endValue, { allowRollover = true, maxRolloverMinutes = 720 } = {}) => {
  const start = toValidDate(startValue);
  const end = toValidDate(endValue);
  if (!start || !end) return null;

  const minutes = Math.round((end.getTime() - start.getTime()) / 60000);
  if (minutes >= 0) return minutes;

  const rolloverMinutes = minutes + 1440;
  return allowRollover && rolloverMinutes <= maxRolloverMinutes ? rolloverMinutes : 0;
};

const toValidDate = value => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const valueString = String(value);
  const normalizedDbUtc = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?$/.test(valueString)
    ? `${valueString.replace(' ', 'T')}Z`
    : valueString;

  const direct = new Date(normalizedDbUtc);
  if (!Number.isNaN(direct.getTime())) return direct;

  if (typeof value === 'string') {
    const normalized = value.includes(' ') ? value.replace(' ', 'T') : value;
    const fallback = new Date(normalized);
    if (!Number.isNaN(fallback.getTime())) return fallback;
  }

  return null;
};

const formatUtcSqlDateTime = (value) => {
  const date = toValidDate(value);
  return date ? date.toISOString().slice(0, 19).replace('T', ' ') : '';
};

const formatDateSafe = (value, pattern) => {
  const date = toValidDate(value);
  return date ? format(date, pattern) : '';
};

const formatDisplayDate = (value) => {
  const date = toValidDate(value);
  return date ? format(date, 'dd.MMM.yyyy') : '';
};

const formatDisplayDateTime = (value) => {
  const date = toValidDate(value);
  return date ? format(date, 'dd.MMM.yyyy HH:mm') : '';
};

const formatRemainingToGoDays = (dueAt, now = Date.now()) => {
  const dueDate = toValidDate(dueAt);
  if (!dueDate) return '';
  const days = Math.max(0, Math.ceil((dueDate.getTime() - now) / 86400000));
  return `${days} ${days === 1 ? 'Day' : 'Days'}`;
};

const getCurrentDisplayDateTime = () => format(new Date(), 'dd.MMM.yyyy HH:mm');

const parseActionMetadata = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const cacheComplaintDeferral = (complaintId, data) => {
  if (!complaintId) return;
  try {
    const cache = JSON.parse(localStorage.getItem('etlb_complaint_deferrals') || '{}');
    cache[String(complaintId)] = { ...(cache[String(complaintId)] || {}), ...data };
    localStorage.setItem('etlb_complaint_deferrals', JSON.stringify(cache));
  } catch {
    // Local cache is only a display fallback for APIs without deferral columns.
  }
};

const parseDisplayDate = (value) => {
  if (!value) return null;
  const direct = toValidDate(value);
  if (direct) return direct;
  const match = String(value).match(/^(\d{1,2})\.([A-Za-z]{3})\.(\d{4})/);
  if (!match) return null;
  const monthIndex = MONTH_ABBREVIATIONS.findIndex(month => month.toLowerCase() === match[2].toLowerCase());
  if (monthIndex < 0) return null;
  const date = new Date(Number(match[3]), monthIndex, Number(match[1]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getMelDiscoveryBaseline = (value) => {
  const date = parseDisplayDate(value) || new Date();
  date.setHours(0, 1, 0, 0);
  return date;
};

const getMelDueDate = (baselineValue, daysValue) => {
  const days = Number.parseInt(daysValue, 10);
  const dueDate = getMelDiscoveryBaseline(baselineValue);
  if (Number.isFinite(days)) {
    dueDate.setDate(dueDate.getDate() + Math.max(0, days));
  }
  return dueDate;
};

const formatUtcDayMonthTime = (value) => {
  const date = toValidDate(value) || new Date();
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = MONTH_ABBREVIATIONS[date.getUTCMonth()] || '';
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const parseFuelDateTimeInput = (value) => {
  const match = String(value || '').trim().match(/^(\d{2})\.([A-Za-z]{3})\.(\d{4}) (\d{2}):(\d{2})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const monthToken = match[2];
  const year = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const month = MONTH_ABBREVIATIONS.findIndex((item) => item.toLowerCase() === monthToken.toLowerCase()) + 1;

  if (month < 1 || month > 12) return null;
  if (hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;

  const daysInMonth = new Date(year, month, 0).getDate();
  if (day < 1 || day > daysInMonth) return null;

  const parsed = new Date(year, month - 1, day, hour, minute, 0, 0);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day ||
    parsed.getHours() !== hour ||
    parsed.getMinutes() !== minute
  ) {
    return null;
  }

  return parsed;
};

// Format time to Zulu format (HH:mmZ) from datetime string
const formatToZulu = (value) => {
  const date = toValidDate(value);
  if (!date) return '';
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}Z`;
};

const formatToUtcTime = (value) => {
  const date = toValidDate(value);
  if (!date) return '';
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Handle Zulu time input with auto-colon
const handleZuluTimeInput = (value) => {
  // Keep only digits and limit to HHmm (4 digits)
  const digitsOnly = value.replace(/\D/g, '').slice(0, 4);

  if (digitsOnly.length === 0) return '';
  if (digitsOnly.length <= 2) {
    const hh = Math.min(Number.parseInt(digitsOnly, 10) || 0, 24);
    return String(hh).padStart(digitsOnly.length, '0');
  }
  if (digitsOnly.length === 3) {
    const hh = Math.min(Number.parseInt(digitsOnly.slice(0, 2), 10) || 0, 24);
    const minuteTens = hh === 24 ? '0' : digitsOnly.slice(2);
    return `${String(hh).padStart(2, '0')}:${minuteTens}`;
  }

  const rawHours = Number.parseInt(digitsOnly.slice(0, 2), 10) || 0;
  const rawMinutes = Number.parseInt(digitsOnly.slice(2), 10) || 0;
  const hours = Math.min(rawHours, 24);
  let minutes = Math.min(rawMinutes, 59);
  if (hours === 24 && minutes > 0) minutes = 0;

  // Only show trailing Z when time is complete (4 digits)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}Z`;
};

const handleUtcTimeInput = (value) => handleZuluTimeInput(value).replace(/Z$/, '');

const formatIcaoInput = (value) => value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
const DEICING_FLUID_TYPES = ['TYPE I', 'TYPE II', 'TYPE IV'];
const ROW_EXIT_ANIMATION_MS = 240;
const DISPATCH_REASONS = ['[NEW] NEW ISSUE', '[REPEAT] REPEAT ITEM', '[MEL] MEL DEFERRAL'];
const MEL_FIXED_DAYS = { B: '3', C: '10', D: '120' };
const getMelDisplayLabel = item => `${item.reference} - ${item.title || item.base_title || ''}`.trim();
const createMelNode = (code, title) => ({
  code,
  title: title || code,
  count: 0,
  sections: new Map(),
  subsections: new Map(),
  bases: new Map(),
  items: []
});
const sortMelNodes = nodes => [...nodes].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
const getMelOperatorForRegistration = (registration) => {
  const value = String(registration || '').trim().toUpperCase();
  if (value.startsWith('9H-')) return 'WMT';
  return 'WZZ';
};
const filterMelItemsForAircraft = (items, registration) => {
  const operatorCode = getMelOperatorForRegistration(registration);
  return (items || []).filter(item => String(item.operator_code || 'WZZ').toUpperCase() === operatorCode);
};
const getMelItemKey = (item, index = 0) => [
  item.operator_code || 'WZZ',
  item.reference,
  item.applicability || '',
  index
].join('-');
const buildMelTree = (items) => {
  const chapters = new Map();

  items.forEach(item => {
    if (!chapters.has(item.ata_chapter)) {
      chapters.set(item.ata_chapter, createMelNode(item.ata_chapter, item.ata_chapter_title));
    }
    const chapter = chapters.get(item.ata_chapter);
    chapter.count += 1;

    if (!chapter.sections.has(item.ata_section)) {
      chapter.sections.set(item.ata_section, createMelNode(item.ata_section, item.ata_section_title));
    }
    const section = chapter.sections.get(item.ata_section);
    section.count += 1;

    if (item.ata_subsection) {
      if (!section.subsections.has(item.ata_subsection)) {
        section.subsections.set(item.ata_subsection, createMelNode(item.ata_subsection, item.ata_subsection_title));
      }
      const subsection = section.subsections.get(item.ata_subsection);
      subsection.count += 1;
      if (!subsection.bases.has(item.base_number)) {
        subsection.bases.set(item.base_number, createMelNode(item.base_number, item.base_title));
      }
      const base = subsection.bases.get(item.base_number);
      base.count += 1;
      base.items.push(item);
    } else {
      if (!section.bases.has(item.base_number)) {
        section.bases.set(item.base_number, createMelNode(item.base_number, item.base_title));
      }
      const base = section.bases.get(item.base_number);
      base.count += 1;
      base.items.push(item);
    }
  });

  return sortMelNodes(chapters.values()).map(chapter => ({
    ...chapter,
    sections: sortMelNodes(chapter.sections.values()).map(section => ({
      ...section,
      subsections: sortMelNodes(section.subsections.values()).map(subsection => ({
        ...subsection,
        bases: sortMelNodes(subsection.bases.values())
      })),
      bases: sortMelNodes(section.bases.values())
    }))
  }));
};
const getOilMaxQtForAircraft = (acType) => {
  const type = String(acType || '').toUpperCase();
  if (type.includes('A320-271N') || type.includes('A321-271N')) return 35;
  if (type.includes('A320-232') || type.includes('A321-231')) return 27.5;
  return 35;
};
const formatOilQuantityInput = (value, maxQt) => {
  const normalized = String(value || '').replace(',', '.');
  const sanitized = normalized.replace(/[^0-9.]/g, '');
  const dotIndex = sanitized.indexOf('.');
  const rebuilt = dotIndex === -1
    ? sanitized
    : `${sanitized.slice(0, dotIndex)}.${sanitized.slice(dotIndex + 1).replace(/\./g, '')}`;
  if (!rebuilt) return '';
  const parsed = Number.parseFloat(rebuilt);
  if (!Number.isFinite(parsed)) return '';
  if (parsed > maxQt) return String(maxQt);
  if (parsed < 0) return '0';
  return rebuilt;
};
const isOilQuantityValid = (value, maxQt) => {
  if (value === '' || value == null) return false;
  const parsed = Number.parseFloat(String(value).replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= maxQt;
};
const createEmptyDeicingRecord = (recordNumber = 1) => ({
  record_number: recordNumber,
  fluid_type: '',
  mixture: '',
  quantity: '',
  start_utc: '',
  holdover_min: '',
  holdover_max: ''
});

const Chevron = () => <span className="ogm-chevron" aria-hidden="true">›</span>;

const defaultSecuritySearchState = { performed: false, is_na: true, performed_at: null };

const TaskRow = ({ label, statusText, isComplete, onClick, rightElement, noChevron, iconSrc, neutral = false, iconClassName = '' }) => (
  <div className={`ogm-item ${neutral ? '' : (isComplete ? 'complete' : 'incomplete')}`} onClick={onClick}>
    <div className="ogm-left">
      <span className="ogm-icon-placeholder" aria-hidden="true">
        {iconSrc && <img src={iconSrc} alt="" className={`ogm-icon-img app-icon ${iconClassName}`.trim()} />}
      </span>
      <span>{label}</span>
    </div>
    <div className="ogm-right">
      {statusText && <span className="ogm-status-text">{statusText}</span>}
      {rightElement}
      {!noChevron && <Chevron />}
    </div>
  </div>
);

const OnGroundMode = ({ flightId, initialFlight, allowInitialFlightFallback = false, onBack, onAccepted, onFlightEnded, refreshSignal = 0 }) => {
  const { aircraft, user } = useAuth();
  const [view, setView] = useState('main');
  const [viewTransitionDirection, setViewTransitionDirection] = useState('none');
  const [flight, setFlight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toGoNow, setToGoNow] = useState(Date.now());
  const isAdmin = isAdminRole(user?.role);
  const defaultCaptainName = useMemo(() => getCrewNameFromUser(user), [user]);
  const getDispatchTransferBaseline = useCallback(() => {
    const currentFlight = flight?.flight || {};
    return {
      transferFh: formatAircraftFlightHours(firstUseful(
        currentFlight.aircraft_flight_hours,
        initialFlight?.aircraft_flight_hours,
        aircraft?.flight_hours,
        currentFlight.flight_hours,
        initialFlight?.flight_hours
      )),
      transferFc: formatAircraftFlightCycles(firstUseful(
        currentFlight.aircraft_flight_cycles,
        initialFlight?.aircraft_flight_cycles,
        aircraft?.flight_cycles,
        currentFlight.flight_cycles,
        initialFlight?.flight_cycles
      ))
    };
  }, [
    aircraft?.flight_hours,
    aircraft?.flight_cycles,
    flight?.flight,
    initialFlight?.aircraft_flight_hours,
    initialFlight?.aircraft_flight_cycles,
    initialFlight?.flight_hours,
    initialFlight?.flight_cycles
  ]);

  // Forms
  const [flightData, setFlightData] = useState({
    flight_number: '',
    from_airport: '',
    to_airport: '',
    scheduled_time_departure: '',
    time_off_block: '', time_takeoff: '', time_landing: '', time_on_block: '',
    full_stop_landings: 0, touch_and_go: 0, total_landings: 0,
    edto: false, pushback: false
  });
  // Flight data time display values (formatted as HH:mm UTC)
  const [zuluTimes, setZuluTimes] = useState({
    scheduled_time_departure: '',
    time_off_block: '',
    time_takeoff: '',
    time_landing: '',
    time_on_block: ''
  });
  const [flightDataError, setFlightDataError] = useState('');
  const [crew, setCrew] = useState(() => [{ full_name: defaultCaptainName, title: 'Captain' }]);
  const [fuelDateTime, setFuelDateTime] = useState('');
  const [fuelDetail, setFuelDetail] = useState({
    noUpliftOnLeg: false,
    remaining: { left: '', center: '0', right: '0', other: '0' },
    beforeFueling: { left: '', center: '0', right: '0', other: '0' }
  });
  const [fuelUplifts, setFuelUplifts] = useState([createEmptyUplift(1)]);
  const [oil, setOil] = useState({ eng1_oil_added: '', eng2_oil_added: '', apu_oil_added: '' });
  const [deicingRecords, setDeicingRecords] = useState([createEmptyDeicingRecord(1)]);
  const [deicingFluidDropdownOpenIndex, setDeicingFluidDropdownOpenIndex] = useState(null);
  const [fuelTypeDropdownOpenIndex, setFuelTypeDropdownOpenIndex] = useState(null);
  const [delay, setDelay] = useState({ delay_code: '', delay_description: '', delay_minutes: '' });
  const scrollPositionsRef = useRef({});
  const [removingCrewIndex, setRemovingCrewIndex] = useState(null);
  const [removingFuelIndex, setRemovingFuelIndex] = useState(null);
  const [removingDeicingIndex, setRemovingDeicingIndex] = useState(null);
  const [removingDelayId, setRemovingDelayId] = useState(null);
  const [animateCrewAddBtn, setAnimateCrewAddBtn] = useState(false);
  const [animateFuelAddBtn, setAnimateFuelAddBtn] = useState(false);
  const [animateDeicingAddBtn, setAnimateDeicingAddBtn] = useState(false);
  const [securitySearch, setSecuritySearch] = useState(defaultSecuritySearchState);
  const [securityUtcNow, setSecurityUtcNow] = useState(() => new Date());
  const [preFlightChecked, setPreFlightChecked] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  
  const [complaints, setComplaints] = useState([]);
  const [ataChapters, setAtaChapters] = useState([]);
  const [newComplaint, setNewComplaint] = useState({
    type: 'Pilot', ata_chapter: '', defect_description: ''
  });
  const [similarComplaintFilterField, setSimilarComplaintFilterField] = useState('work_order_number');
  const [similarComplaintSearch, setSimilarComplaintSearch] = useState('');
  const [similarComplaintDateFrom, setSimilarComplaintDateFrom] = useState('');
  const [similarComplaintDateTo, setSimilarComplaintDateTo] = useState('');
  const [similarComplaintFilterOpen, setSimilarComplaintFilterOpen] = useState(false);
  const [similarComplaintOpenCalendar, setSimilarComplaintOpenCalendar] = useState(null);
  const [similarComplaintCalendarMonths, setSimilarComplaintCalendarMonths] = useState({
    from: new Date(),
    to: new Date()
  });
  const [selectedComplaintId, setSelectedComplaintId] = useState(null);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaintActions, setComplaintActions] = useState([]);
  const complaintDetailReturnView = 'main';
  const [aircraftStatusInitialComplaint, setAircraftStatusInitialComplaint] = useState(null);
  const [aircraftStatusReturnView, setAircraftStatusReturnView] = useState('main');
  const [complaintTransitionUnderlay, setComplaintTransitionUnderlay] = useState(null);
  const [voidComplaintModalOpen, setVoidComplaintModalOpen] = useState(false);
  const [dispatchComplaint, setDispatchComplaint] = useState({
    category: 'MEL',
    quickReference: '',
    melType: 'A',
    documentReference: '',
    numberInstalled: '',
    numberRequired: '',
    placardRequired: null,
    installedRequired: '',
    transferDate: '',
    transferFh: '',
    transferFc: '',
    toGoDays: '0',
    toGoFh: '',
    toGoFc: '',
    dueAtDate: '',
    dueAtFh: '',
    dueAtFc: '',
    reason: '[NEW] NEW ISSUE',
    actionText: 'DISPATCHED I.A.W. MEL',
    repetitiveInspection: false,
    repetitiveInspectionDescription: 'DO INSPECTION',
    inspectionIntervalDays: '',
    inspectionIntervalFh: '',
    inspectionIntervalFc: '',
    deferralStation: '',
    deferralDate: ''
  });
  const [melItems, setMelItems] = useState([]);
  const [melItemsLoading, setMelItemsLoading] = useState(false);
  const [melSearch, setMelSearch] = useState('');
  const [melPath, setMelPath] = useState({ chapter: '', section: '', subsection: '', base: '' });
  const [melPreviewItem, setMelPreviewItem] = useState(null);
  const similarComplaintFilterRef = useRef(null);
  const similarComplaintCalendarRef = useRef(null);
  const complaintTransitionTimerRef = useRef(null);
  const melLoadStartedRef = useRef(false);
  const complaintActionPendingRef = useRef(false);
  const initialFlightLoadCompleteRef = useRef(false);
  const dispatchLibrary = 'MEL';
  const withDefaultCaptainCrew = useCallback((crewList = []) => {
    const hasCrewList = Array.isArray(crewList) && crewList.length > 0;
    const source = hasCrewList ? crewList : [{ full_name: '', title: 'Captain' }];
    const next = source.map(member => ({ ...member }));
    let changed = !hasCrewList;
    const captainIndex = next.findIndex(member => String(member.title || '').trim().toLowerCase() === 'captain');

    if (captainIndex < 0) {
      next.unshift({ full_name: defaultCaptainName, title: 'Captain' });
      changed = true;
    } else {
      const captain = next[captainIndex];
      const currentCaptainName = String(captain.full_name || '').trim();
      const cleanCaptainName = stripCrewIdNumbers(currentCaptainName);
      const nextCaptainName = cleanCaptainName || defaultCaptainName;
      if (nextCaptainName && nextCaptainName !== currentCaptainName) {
        next[captainIndex] = { ...captain, full_name: nextCaptainName };
        changed = true;
      }
    }

    return changed ? next : source;
  }, [defaultCaptainName]);
  const shouldPersistDefaultCaptainCrew = useCallback((crewList = []) => {
    if (!Array.isArray(crewList) || crewList.length === 0) return Boolean(defaultCaptainName);

    const captain = crewList.find(member => String(member.title || '').trim().toLowerCase() === 'captain');
    if (!captain) return Boolean(defaultCaptainName);

    const currentCaptainName = String(captain.full_name || '').trim();
    const cleanCaptainName = stripCrewIdNumbers(currentCaptainName);
    return (!currentCaptainName && Boolean(defaultCaptainName)) || (cleanCaptainName && cleanCaptainName !== currentCaptainName);
  }, [defaultCaptainName]);
  const getCrewForSave = useCallback((crewList = crew) => (
    withDefaultCaptainCrew(crewList).filter(member => String(member.full_name || '').trim())
  ), [crew, withDefaultCaptainCrew]);
  const viewContainerClass = [
    'ogm-container',
    viewTransitionDirection === 'none' ? '' : `ogm-view-${viewTransitionDirection}`,
    view !== 'main' ? 'ogm-subview-overlay' : ''
  ].filter(Boolean).join(' ');
  const melTree = useMemo(() => buildMelTree(melItems), [melItems]);
  const melSearchResults = useMemo(() => {
    const query = melSearch.trim().toLowerCase();
    if (!query) return [];
    return melItems
      .filter(item => String(item.search_text || '').includes(query))
      .slice(0, 120);
  }, [melItems, melSearch]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setToGoNow(Date.now()), 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    setCrew(prev => withDefaultCaptainCrew(prev));
  }, [withDefaultCaptainCrew]);

  useEffect(() => {
    const dashboardLayout = document.querySelector('.dashboard-layout');
    if (!dashboardLayout) return undefined;

    if (view !== 'main') {
      dashboardLayout.classList.add('dashboard-subview-open');
    } else {
      dashboardLayout.classList.remove('dashboard-subview-open');
    }

    return () => {
      dashboardLayout.classList.remove('dashboard-subview-open');
    };
  }, [view]);

  const captureCurrentScrollPosition = useCallback(() => {
    const scrollArea = document.querySelector('.dashboard-view-panel > .ogm-container > .ogm-scroll-area')
      || document.querySelector('.ogm-container > .ogm-scroll-area');
    if (scrollArea) {
      scrollPositionsRef.current[view] = scrollArea.scrollTop;
    }
  }, [view]);

  const openSubView = useCallback((nextView) => {
    captureCurrentScrollPosition();
    if (nextView === 'fuel' || nextView === 'oil') {
      setFuelDateTime(getCurrentDisplayDateTime());
    }
    if (nextView === 'aircraftStatus') {
      setAircraftStatusInitialComplaint(null);
      setAircraftStatusReturnView('main');
    }
    setViewTransitionDirection('forward');
    setView(nextView);
  }, [captureCurrentScrollPosition]);

  const navigateBackToMain = useCallback(() => {
    captureCurrentScrollPosition();
    setViewTransitionDirection('back');
    setView('main');
  }, [captureCurrentScrollPosition]);

  const navigateBackFromAircraftStatus = useCallback(() => {
    captureCurrentScrollPosition();
    setViewTransitionDirection('back');
    if (aircraftStatusReturnView === 'createComplaint') {
      setView('createComplaint');
      return;
    }
    if (aircraftStatusReturnView === 'onGround') {
      setView('main');
      return;
    }
    setView('main');
  }, [aircraftStatusReturnView, captureCurrentScrollPosition]);

  const navigateBackFromComplaintDetail = useCallback(() => {
    captureCurrentScrollPosition();
    setViewTransitionDirection('back');
    setView(complaintDetailReturnView || 'main');
  }, [captureCurrentScrollPosition, complaintDetailReturnView]);

  const loadFlight = useCallback(async ({ silent = false } = {}) => {
    if (!flightId) return;
    if (!silent) setLoading(true);
    try {
      const res = await getFlight(flightId);
      const apiFlight = res.data.flight || {};
      const fallbackFlight = allowInitialFlightFallback
        ? {
            ...(getCreatedFlightPayload(flightId) || {}),
            ...(initialFlight || {})
          }
        : {};
      const f = {
        ...apiFlight,
        flight_number: apiFlight.flight_number || fallbackFlight.flight_number || '',
        from_airport: apiFlight.from_airport || fallbackFlight.from_airport || '',
        to_airport: apiFlight.to_airport || fallbackFlight.to_airport || '',
        date_of_flight: apiFlight.date_of_flight || fallbackFlight.date_of_flight || ''
      };
      const mergedFlightData = {
        ...res.data,
        flight: f
      };
      setFlight(mergedFlightData);
      setFlightData({
        flight_number: f.flight_number || '',
        from_airport: f.from_airport || '',
        to_airport: f.to_airport || '',
        scheduled_time_departure: formatUtcSqlDateTime(f.scheduled_time_departure),
        time_off_block: formatUtcSqlDateTime(f.time_off_block),
        time_takeoff: formatUtcSqlDateTime(f.time_takeoff),
        time_landing: formatUtcSqlDateTime(f.time_landing),
        time_on_block: formatUtcSqlDateTime(f.time_on_block),
        full_stop_landings: f.full_stop_landings || 0,
        touch_and_go: f.touch_and_go || 0,
        total_landings: f.total_landings || 0,
        edto: f.edto || false,
        pushback: f.pushback || false
      });
      setFuelDateTime(formatDisplayDateTime(f.date_of_flight) || getCurrentDisplayDateTime());
      // Set Zulu times for display
      setZuluTimes({
        scheduled_time_departure: formatToUtcTime(f.scheduled_time_departure),
        time_off_block: formatToUtcTime(f.time_off_block),
        time_takeoff: formatToUtcTime(f.time_takeoff),
        time_landing: formatToUtcTime(f.time_landing),
        time_on_block: formatToUtcTime(f.time_on_block)
      });
      const loadedCrew = res.data.crew || [];
      const normalizedCrew = withDefaultCaptainCrew(loadedCrew);
      setCrew(normalizedCrew);
      if (shouldPersistDefaultCaptainCrew(loadedCrew)) {
        try {
          await saveCrew(flightId, normalizedCrew.filter(member => String(member.full_name || '').trim()));
        } catch (crewSaveErr) {
          console.error('Error auto-saving default captain:', crewSaveErr);
        }
      }
      // Load new fuel data structure
      // Helper to format fuel values (remove trailing zeros)
      const formatLoadedFuel = (val, defaultVal = '') => {
        if (val == null) return defaultVal;
        const num = Number(val);
        // Remove trailing zeros: 0.0 -> 0, 1500.0 -> 1500, 0.800 -> 0.8
        return String(parseFloat(num.toFixed(10)));
      };
      const getFuelRecordTotal = (record, prefix) => (
        toFuelNumber(record?.[`${prefix}_left`]) +
        toFuelNumber(record?.[`${prefix}_center`]) +
        toFuelNumber(record?.[`${prefix}_right`]) +
        toFuelNumber(record?.[`${prefix}_other`])
      );
      const applyInitialRemainingFuel = (total) => {
        setFuelDetail({
          noUpliftOnLeg: false,
          remaining: {
            left: formatLoadedFuel(total, '0'),
            center: '0',
            right: '0',
            other: '0'
          },
          beforeFueling: {
            left: formatLoadedFuel(total, '0'),
            center: '0',
            right: '0',
            other: '0'
          }
        });
      };

      if (res.data.fuelData) {
        const fd = res.data.fuelData;
        const remainingTotal = getFuelRecordTotal(fd, 'remaining');
        const beforeFuelingTotalLoaded = getFuelRecordTotal(fd, 'before_fueling');
        const normalizedBeforeFueling = beforeFuelingTotalLoaded > 0 ? beforeFuelingTotalLoaded : remainingTotal;
        setFuelDetail({
          noUpliftOnLeg: false,
          remaining: {
            left: formatLoadedFuel(remainingTotal, '0'),
            center: '0',
            right: '0',
            other: '0'
          },
          beforeFueling: {
            left: formatLoadedFuel(normalizedBeforeFueling, '0'),
            center: '0',
            right: '0',
            other: '0'
          }
        });
      } else if (res.data.previousArrivalFuel) {
        const previousArrivalTotal = getFuelRecordTotal(res.data.previousArrivalFuel, 'arrival_fuel');
        applyInitialRemainingFuel(previousArrivalTotal);
      }
      // Load fuel uplifts
      if (res.data.fuelUplifts && res.data.fuelUplifts.length > 0) {
        setFuelUplifts(res.data.fuelUplifts.map(u => ({
          uplift_number: u.uplift_number,
          departure_fuel_left: formatLoadedFuel(
            toFuelNumber(u.departure_fuel_left) +
            toFuelNumber(u.departure_fuel_center) +
            toFuelNumber(u.departure_fuel_right) +
            toFuelNumber(u.departure_fuel_other)
          ),
          departure_fuel_center: '0',
          departure_fuel_right: '0',
          departure_fuel_other: '0',
          bowser_liters: formatLoadedFuel(u.bowser_liters),
          density: formatLoadedFuel(u.density, '0.8'),
          fuel_bill_no: u.fuel_bill_no || '',
          fuel_type: u.fuel_type || 'Jet A1',
          fuel_unit: u.fuel_unit || 'metric'
        })));
      } else {
        setFuelUplifts([createEmptyUplift(1)]);
      }
      if (res.data.oil) {
        setOil({
          eng1_oil_added: formatLoadedFuel(res.data.oil.eng1_oil_added),
          eng2_oil_added: formatLoadedFuel(res.data.oil.eng2_oil_added),
          apu_oil_added: formatLoadedFuel(res.data.oil.apu_oil_added)
        });
      } else {
        setOil({ eng1_oil_added: '', eng2_oil_added: '', apu_oil_added: '' });
      }
      if (Array.isArray(res.data.deicingRecords) && res.data.deicingRecords.length > 0) {
        setDeicingRecords(res.data.deicingRecords.map((record, idx) => ({
          record_number: record.record_number || (idx + 1),
          fluid_type: record.fluid_type || '',
          mixture: record.mixture || record.concentration || '',
          quantity: record.quantity != null ? String(record.quantity) : '',
          start_utc: record.start_utc || formatToZulu(record.performed_at),
          holdover_min: record.holdover_min != null ? String(record.holdover_min) : '',
          holdover_max: record.holdover_max != null ? String(record.holdover_max) : ''
        })));
      } else if (res.data.deicing) {
        setDeicingRecords([{
          record_number: 1,
          fluid_type: res.data.deicing.fluid_type || '',
          mixture: res.data.deicing.concentration || '',
          quantity: res.data.deicing.quantity != null ? String(res.data.deicing.quantity) : '',
          start_utc: formatToZulu(res.data.deicing.performed_at),
          holdover_min: '',
          holdover_max: ''
        }]);
      } else {
        setDeicingRecords([createEmptyDeicingRecord(1)]);
      }
      setSecuritySearch(res.data.security ? {
        performed: res.data.security.performed || false,
        is_na: res.data.security.is_na || false,
        performed_at: res.data.security.performed_at || null
      } : defaultSecuritySearchState);
      if (Array.isArray(res.data.delays) && res.data.delays.length > 0) {
        const latestDelay = [...res.data.delays].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
        setDelay(prev => prev.delay_code ? prev : {
          ...prev,
          delay_code: String(latestDelay.delay_code || '')
        });
      }
    } catch (err) {
      console.error('Error loading flight:', err);
    }
    if (!silent) setLoading(false);
  }, [flightId, initialFlight, allowInitialFlightFallback, withDefaultCaptainCrew, shouldPersistDefaultCaptainCrew]);

  useEffect(() => {
    const silent = initialFlightLoadCompleteRef.current;
    initialFlightLoadCompleteRef.current = true;
    loadFlight({ silent });
  }, [loadFlight, refreshSignal]);

  useEffect(() => {
    if (!aircraft) return;
    getComplaints(aircraft.id).then(r => setComplaints(r.data.complaints || [])).catch(() => {});
    getATAChapters().then(r => setAtaChapters(r.data.chapters || [])).catch(() => {});
  }, [aircraft, refreshSignal]);

  const loadMelItems = useCallback(async () => {
    if (melLoadStartedRef.current || melItems.length > 0) return;
    melLoadStartedRef.current = true;
    setMelItemsLoading(true);
    try {
      const res = await getMELItems({
        limit: 2000,
        aircraft_id: aircraft?.id,
        registration: aircraft?.registration
      });
      setMelItems(res.data.items || []);
    } catch (err) {
      console.error(`Error loading ${dispatchLibrary} items:`, err);
      try {
        const fallbackRes = await fetch(`${process.env.PUBLIC_URL || ''}/mel_items.json`);
        if (!fallbackRes.ok) throw new Error(`${dispatchLibrary} fallback failed: ${fallbackRes.status}`);
        const fallbackData = await fallbackRes.json();
        setMelItems(filterMelItemsForAircraft(fallbackData.items || [], aircraft?.registration));
      } catch (fallbackErr) {
        console.error(`Error loading ${dispatchLibrary} fallback data:`, fallbackErr);
      }
    } finally {
      setMelItemsLoading(false);
    }
  }, [aircraft?.id, aircraft?.registration, dispatchLibrary, melItems.length]);

  useEffect(() => {
    melLoadStartedRef.current = false;
    setMelItems([]);
    setMelPreviewItem(null);
    setMelPath({ chapter: '', section: '', subsection: '', base: '' });
  }, [aircraft?.registration, dispatchLibrary]);

  useEffect(() => {
    if (view === 'melBrowser') loadMelItems();
  }, [view, loadMelItems]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (similarComplaintFilterRef.current && !similarComplaintFilterRef.current.contains(event.target)) {
        setSimilarComplaintFilterOpen(false);
      }
      if (similarComplaintCalendarRef.current && !similarComplaintCalendarRef.current.contains(event.target)) {
        setSimilarComplaintOpenCalendar(null);
      }
      if (!event.target.closest('.deicing-type-dropdown')) {
        setDeicingFluidDropdownOpenIndex(null);
      }
      if (!event.target.closest('.fuel-type-dropdown')) {
        setFuelTypeDropdownOpenIndex(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!securitySearch.performed || securitySearch.is_na) return undefined;
    setSecurityUtcNow(new Date());
    const timer = setInterval(() => {
      setSecurityUtcNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [securitySearch.performed, securitySearch.is_na]);

  useEffect(() => () => {
    if (complaintTransitionTimerRef.current) {
      clearTimeout(complaintTransitionTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (viewTransitionDirection === 'none') return undefined;
    const timer = setTimeout(() => {
      setViewTransitionDirection('none');
    }, 320);
    return () => clearTimeout(timer);
  }, [viewTransitionDirection]);

  useLayoutEffect(() => {
    const scrollArea = document.querySelector('.dashboard-view-panel > .ogm-container > .ogm-scroll-area')
      || document.querySelector('.ogm-container > .ogm-scroll-area');
    if (!scrollArea) return undefined;
    const scrollPositions = scrollPositionsRef.current;

    const targetTop = scrollPositions[view] || 0;
    const previousBehavior = scrollArea.style.scrollBehavior;
    scrollArea.style.scrollBehavior = 'auto';
    if (Math.abs(scrollArea.scrollTop - targetTop) > 1) {
      scrollArea.scrollTop = targetTop;
    }
    scrollArea.style.scrollBehavior = previousBehavior;

    const onScroll = () => {
      scrollPositions[view] = scrollArea.scrollTop;
    };

    scrollArea.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollPositions[view] = scrollArea.scrollTop;
      scrollArea.removeEventListener('scroll', onScroll);
    };
  }, [loading, view]);

  // Get max fuel capacity for current aircraft
  const maxFuelCapacity = getMaxFuelForAircraft(aircraft?.ac_type);
  const oilMaxQt = getOilMaxQtForAircraft(aircraft?.ac_type);

  const isFuelValueFilled = (value) => value !== '' && value !== null && value !== undefined && value !== '.';

  // Calculate actual uplift in kg for a card
  const getActualUpliftKg = (cardIndex) => {
    const uplift = fuelUplifts[cardIndex];
    if (!uplift) return 0;
    const enteredAmount = toFuelNumber(uplift.bowser_liters);
    const amountInLiters = (uplift.fuel_unit || 'metric') === 'imperial'
      ? enteredAmount * IMPERIAL_GALLON_TO_LITERS
      : enteredAmount;
    return amountInLiters * toFuelNumber(uplift.density);
  };

  const getFuelAfterUpliftKg = (cardIndex) => {
    const uplift = fuelUplifts[cardIndex];
    if (!uplift) return 0;

    const baseFuelKg = cardIndex === 0
      ? toFuelNumber(fuelDetail.remaining.left)
      : (() => {
        const previousUplift = fuelUplifts[cardIndex - 1];
        if (!previousUplift) return 0;
        return isFuelValueFilled(previousUplift.departure_fuel_left)
          ? toFuelNumber(previousUplift.departure_fuel_left)
          : getFuelAfterUpliftKg(cardIndex - 1);
      })();

    return baseFuelKg + getActualUpliftKg(cardIndex);
  };

  // Check if fuel data is complete (for validation)
  const isFuelComplete = () => {
    if (!isFuelValueFilled(fuelDetail.remaining.left)) return false;
    if (toFuelNumber(fuelDetail.remaining.left) > maxFuelCapacity) return false;

    for (let i = 0; i < fuelUplifts.length; i++) {
      const uplift = fuelUplifts[i];
      if (!isFuelValueFilled(uplift.density)) return false;
      if (!isFuelValueFilled(uplift.bowser_liters)) return false;
      if (!isFuelValueFilled(uplift.departure_fuel_left)) return false;
      if (toFuelNumber(uplift.departure_fuel_left) > maxFuelCapacity) return false;
    }
    return true;
  };

  const hasFuelData = isFuelComplete();

  const hasCrew = crew.some(c => c.full_name.trim());
  const hasFlightData = !!flight?.flight?.scheduled_time_departure;
  const openAndOverdueItems = complaints.filter(isOpenOrOverdueComplaint);
  const securityTimeDisplay = securitySearch.performed && !securitySearch.is_na
    ? formatUtcDayMonthTime(securityUtcNow)
    : '';

  const handleSaveFlightData = async () => {
    setFlightDataError('');
    setSaving(true);
    try {
      await updateFlightData(flightId, flightData);
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleSaveCrew = async () => {
    setSaving(true);
    try {
      const nextCrew = getCrewForSave();
      await saveCrew(flightId, nextCrew);
      setCrew(nextCrew.length ? nextCrew : withDefaultCaptainCrew([]));
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const updateRemainingFuelTotal = (value) => {
    let nextValue = value;
    if (isFuelValueFilled(value)) {
      nextValue = String(Math.min(toFuelNumber(value), maxFuelCapacity));
    }

    setFuelDetail(prev => ({
      ...prev,
      remaining: {
        left: nextValue,
        center: '0',
        right: '0',
        other: '0'
      },
      beforeFueling: {
        left: nextValue,
        center: '0',
        right: '0',
        other: '0'
      }
    }));
  };

  const updateDepartureFuelTotal = (index, value) => {
    let nextValue = value;
    if (isFuelValueFilled(value)) {
      nextValue = String(Math.min(toFuelNumber(value), maxFuelCapacity));
    }
    setFuelUplifts(prev => prev.map((u, i) => (
      i === index
        ? {
          ...u,
          departure_fuel_left: nextValue,
          departure_fuel_center: '0',
          departure_fuel_right: '0',
          departure_fuel_other: '0'
        }
        : u
    )));
  };

  const handleSaveFuel = async () => {
    const parsedFuelDate = parseFuelDateTimeInput(fuelDateTime || getCurrentDisplayDateTime());
    if (!parsedFuelDate) return;
    setSaving(true);
    try {
      // Helper to preserve 0 values (only return null if empty string)
      const toFuelValue = (val) => {
        if (val === '' || val === null || val === undefined) return null;
        return toFuelNumber(val);
      };

      const payload = {
        date_of_flight: parsedFuelDate.toISOString(),
        fuelData: {
          no_uplift_on_leg: false,
          remaining_left: toFuelValue(fuelDetail.remaining.left),
          remaining_center: toFuelValue(fuelDetail.remaining.center),
          remaining_right: toFuelValue(fuelDetail.remaining.right),
          remaining_other: toFuelValue(fuelDetail.remaining.other),
          before_fueling_left: toFuelValue(fuelDetail.beforeFueling.left),
          before_fueling_center: toFuelValue(fuelDetail.beforeFueling.center),
          before_fueling_right: toFuelValue(fuelDetail.beforeFueling.right),
          before_fueling_other: toFuelValue(fuelDetail.beforeFueling.other)
        },
        uplifts: fuelUplifts.map(u => ({
          uplift_number: u.uplift_number,
          departure_fuel_left: toFuelValue(u.departure_fuel_left),
          departure_fuel_center: toFuelValue(u.departure_fuel_center),
          departure_fuel_right: toFuelValue(u.departure_fuel_right),
          departure_fuel_other: toFuelValue(u.departure_fuel_other),
          bowser_liters: toFuelValue(u.bowser_liters),
          density: toFuelNumber(u.density) || 0.8,
          fuel_bill_no: u.fuel_bill_no || null,
          fuel_type: u.fuel_type || 'Jet A1',
          fuel_unit: u.fuel_unit || 'metric'
        }))
      };
      await saveFuel(flightId, payload);
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleSaveOil = async () => {
    const parsedOilDate = parseFuelDateTimeInput(fuelDateTime || getCurrentDisplayDateTime());
    if (!parsedOilDate) return;
    setSaving(true);
    try {
      await saveOil(flightId, {
        ...oil,
        date_of_flight: parsedOilDate.toISOString()
      });
      try {
        await loadFlight();
      } catch (loadErr) {
        console.error('Error reloading flight after oil save:', loadErr);
      }
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDeicing = async () => {
    setSaving(true);
    try {
      await saveDeicing(flightId, {
        records: deicingRecords.map((record, idx) => ({
          record_number: idx + 1,
          fluid_type: record.fluid_type || '',
          mixture: record.mixture || null,
          quantity: record.quantity === '' ? null : Number(record.quantity),
          start_utc: record.start_utc || null,
          holdover_min: record.holdover_min === '' ? null : Number(record.holdover_min),
          holdover_max: record.holdover_max === '' ? null : Number(record.holdover_max)
        }))
      });
      await loadFlight();
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const handleSaveComplaint = async () => {
    if (!newComplaint.defect_description.trim()) return;
    setSaving(true);
    try {
      await createComplaint({
        aircraft_id: aircraft.id,
        flight_id: flightId,
        ...newComplaint,
        section: 'mcc_approved'
      });
      setNewComplaint({ type: 'Pilot', ata_chapter: '', defect_description: '' });
      const res = await getComplaints(aircraft.id);
      setComplaints(res.data.complaints || []);
      navigateBackToMain();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const loadComplaintActions = async (complaintId) => {
    if (!complaintId || !complaintActionsEnabled) {
      setComplaintActions([]);
      return [];
    }
    try {
      const res = await getComplaintActions(complaintId);
      const actions = res.data.actions || [];
      setComplaintActions(actions);
      return actions;
    } catch (err) {
      if (err.response?.status !== 404) console.error(err);
      setComplaintActions([]);
      return [];
    }
  };

  const openComplaintDetail = async (complaintId) => {
    if (!complaintId) return;
    captureCurrentScrollPosition();
    setViewTransitionDirection('forward');
    setComplaintTransitionUnderlay(null);
    setAircraftStatusReturnView(view === 'createComplaint' ? 'createComplaint' : 'main');
    setAircraftStatusInitialComplaint(complaints.find(item => String(item.id) === String(complaintId)) || { id: complaintId });
    setView('aircraftStatus');
  };

  const openOnGroundComplaintDetail = async (complaintId) => {
    if (!complaintId) return;
    captureCurrentScrollPosition();
    setViewTransitionDirection('forward');
    setComplaintTransitionUnderlay(null);
    const complaint = complaints.find(item => String(item.id) === String(complaintId)) || { id: complaintId };
    if (String(complaint?.status || '').toUpperCase() === 'DEFERRED') {
      setAircraftStatusReturnView('onGround');
      setAircraftStatusInitialComplaint(complaint);
      setView('aircraftStatus');
      return;
    }
    setView('complaintDetail');
    setSelectedComplaintId(complaintId);
    setSelectedComplaint(complaint);
    setComplaintActions([]);
    try {
      const [complaintRes] = await Promise.all([
        getComplaint(complaintId),
        loadComplaintActions(complaintId)
      ]);
      setSelectedComplaint(complaintRes.data.complaint || null);
    } catch (err) {
      console.error(err);
    }
  };

  const reloadComplaintDetail = async () => {
    if (!selectedComplaintId) return;
    setSaving(true);
    try {
      const [complaintRes] = await Promise.all([
        getComplaint(selectedComplaintId),
        loadComplaintActions(selectedComplaintId)
      ]);
      setSelectedComplaint(complaintRes.data.complaint || null);
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  const getDefaultDispatchComplaint = (complaint = selectedComplaint) => {
    const now = new Date();
    const baselineDate = new Date(now);
    baselineDate.setHours(0, 1, 0, 0);
    const transferDate = format(baselineDate, 'dd.MMM.yyyy');
    const deferralDate = format(now, 'dd.MMM.yyyy HH:mm');
    const transferBaseline = getDispatchTransferBaseline();
    return {
      category: 'MEL',
      quickReference: '',
      melType: 'A',
      documentReference: '',
      numberInstalled: '',
      numberRequired: '',
      placardRequired: null,
      installedRequired: '',
      transferDate,
      transferFh: transferBaseline.transferFh,
      transferFc: transferBaseline.transferFc,
      toGoDays: '0',
      toGoFh: '',
      toGoFc: '',
      dueAtDate: format(getMelDueDate(transferDate, '0'), 'dd.MMM.yyyy'),
      dueAtFh: '',
      dueAtFc: '',
      reason: '[NEW] NEW ISSUE',
      actionText: '',
      repetitiveInspection: false,
      repetitiveInspectionDescription: 'DO INSPECTION',
      inspectionIntervalDays: '',
      inspectionIntervalFh: '',
      inspectionIntervalFc: '',
      deferralStation: flight?.flight?.from_airport || '',
      deferralDate,
      defectDescription: complaint?.defect_description || ''
    };
  };

  const selectComplaintForAction = (complaint) => {
    if (!complaint) return;
    setSelectedComplaintId(complaint.id);
    setSelectedComplaint(complaint);
  };

  const openDispatchComplaintPage = (complaint = selectedComplaint) => {
    if (!complaint) return;
    if (isComplaintLocked(complaint) && !isAdmin) return;
    selectComplaintForAction(complaint);
    setDispatchComplaint(getDefaultDispatchComplaint(complaint));
    setViewTransitionDirection('forward');
    setView('dispatchComplaint');
  };

  const openMelBrowser = () => {
    setMelSearch('');
    setMelPath({ chapter: '', section: '', subsection: '', base: '' });
    setMelPreviewItem(null);
    setViewTransitionDirection('forward');
    setView('melBrowser');
  };

  const updateDispatchComplaint = (patch) => {
    setDispatchComplaint(prev => {
      const next = { ...prev, ...patch };
      const nextCategory = String(next.category || '').toUpperCase();

      if (patch.category) {
        const previousCategory = String(prev.category || '').toUpperCase();
        if (nextCategory === 'DD') {
          const ddType = ['A', 'D'].includes(String(prev.melType || '').toUpperCase())
            ? String(prev.melType || '').toUpperCase()
            : 'D';
          next.melType = ddType;
          next.quickReference = '';
          next.documentReference = '';
          next.numberInstalled = '';
          next.numberRequired = '';
          next.placardRequired = null;
          next.installedRequired = '';
          next.defectDescription = selectedComplaint?.defect_description || next.defectDescription || '';
          if (!next.actionText || next.actionText === 'DISPATCHED I.A.W. MEL') {
            next.actionText = 'DISPATCHED AS DD';
          }
          next.toGoDays = ddType === 'D' ? MEL_FIXED_DAYS.D : '';
        } else if (previousCategory === 'DD') {
          next.melType = 'A';
          next.toGoDays = '';
          next.actionText = '';
        } else if (previousCategory && previousCategory !== nextCategory) {
          next.quickReference = '';
          next.documentReference = '';
          next.numberInstalled = '';
          next.numberRequired = '';
          next.placardRequired = null;
          next.installedRequired = '';
          next.defectDescription = selectedComplaint?.defect_description || next.defectDescription || '';
          next.actionText = '';
        }
        next.toGoFh = '';
        next.toGoFc = '';
      }

      if (patch.melType || patch.category) {
        if (nextCategory === 'DD' && !['A', 'D'].includes(String(next.melType || '').toUpperCase())) {
          next.melType = 'D';
        }
        const nextType = String(next.melType || '').toUpperCase();
        if (MEL_FIXED_DAYS[nextType]) {
          next.toGoDays = MEL_FIXED_DAYS[nextType];
        } else if (nextType === 'A' && (patch.melType || prev.melType !== 'A')) {
          next.toGoDays = '';
        }
        next.melType = nextType;
        next.toGoFh = '';
        next.toGoFc = '';
      }
      next.dueAtDate = format(getMelDueDate(next.transferDate, next.toGoDays), 'dd.MMM.yyyy');
      return next;
    });
  };

  const selectMelItem = (item) => {
    if (!item) return;
    const melType = item.mel_type || 'A';
    const requirements = getMelRequirementData(item);
    updateDispatchComplaint({
      quickReference: item.reference,
      melType,
      documentReference: item.reference,
      numberInstalled: requirements.numberInstalled === '-' ? '' : requirements.numberInstalled,
      numberRequired: requirements.numberRequired === '-' ? '' : requirements.numberRequired,
      placardRequired: item.placard_required ?? null,
      installedRequired: item.installed_required || '',
      toGoDays: MEL_FIXED_DAYS[melType] || '',
      defectDescription: `${item.reference} / ${item.title || item.base_title || ''}`,
      actionText: ''
    });
    setViewTransitionDirection('back');
    setView('dispatchComplaint');
  };

  const beginComplaintAction = () => {
    if (complaintActionPendingRef.current) return false;
    complaintActionPendingRef.current = true;
    setSaving(true);
    return true;
  };

  const endComplaintAction = () => {
    complaintActionPendingRef.current = false;
    setSaving(false);
  };

  const handleToggleComplaintLock = async () => {
    if (!selectedComplaintId || !isAdmin) return;
    if (!beginComplaintAction()) return;
    try {
      const nextLocked = !isComplaintLocked(selectedComplaint);
      const res = await updateComplaint(selectedComplaintId, {
        is_locked: nextLocked,
        action_type: 'LOCK'
      });
      setSelectedComplaint(res.data.complaint || null);
      await loadComplaintActions(selectedComplaintId);
      const complaintsRes = await getComplaints(aircraft.id);
      setComplaints(complaintsRes.data.complaints || []);
    } catch (err) {
      console.error(err);
    } finally {
      endComplaintAction();
    }
  };

  const handleVoidComplaint = async () => {
    if (!selectedComplaintId) return;
    if (isComplaintLocked(selectedComplaint) && !isAdmin) {
      setVoidComplaintModalOpen(false);
      return;
    }
    if (!beginComplaintAction()) return;
    setVoidComplaintModalOpen(false);
    try {
      const actionText = VOID_ACTION_TEXT;
      const res = await updateComplaint(selectedComplaintId, {
        status: 'CLOSED',
        section: 'voided',
        mcc_reference: actionText,
        action_type: 'VOID',
        action_text: actionText,
        action_metadata: {
          workstep: 'Defect Description',
          action: actionText
        }
      });
      setSelectedComplaint(res.data.complaint || null);
      await loadComplaintActions(selectedComplaintId);
      const complaintsRes = await getComplaints(aircraft.id);
      setComplaints(complaintsRes.data.complaints || []);
      if (view !== 'main') navigateBackToMain();
    } catch (err) {
      console.error(err);
    } finally {
      endComplaintAction();
    }
  };

  const handleSaveDispatchComplaint = async () => {
    if (!selectedComplaintId) return;
    if (isComplaintLocked(selectedComplaint) && !isAdmin) return;
    if (!beginComplaintAction()) return;
    try {
      const deferralCategory = String(dispatchComplaint.category || '').toUpperCase();
      const isDdDeferral = deferralCategory === 'DD';
      const deferredSection = isDdDeferral ? 'non_airworthy_deferred' : 'airworthy_deferred';
      const deferralType = isDdDeferral && !['A', 'D'].includes(String(dispatchComplaint.melType || '').toUpperCase())
        ? 'D'
        : String(dispatchComplaint.melType || '').toUpperCase();
      const dueDate = getMelDueDate(dispatchComplaint.transferDate, dispatchComplaint.toGoDays);
      const dueHours = Number.parseInt(dispatchComplaint.toGoDays, 10);
      const dispatchActionText = dispatchComplaint.actionText?.trim() || (isDdDeferral ? 'DISPATCHED AS DD' : 'DISPATCHED I.A.W. MEL');
      const melCdlRef = isDdDeferral ? '' : (dispatchComplaint.quickReference || '');
      const documentReference = isDdDeferral ? '' : dispatchComplaint.documentReference;
      const deferralPayload = {
        deferral_category: deferralCategory || dispatchComplaint.category,
        deferral_type: deferralType,
        deferral_station: dispatchComplaint.deferralStation,
        deferral_reason: dispatchComplaint.reason,
        deferral_date: dispatchComplaint.deferralDate,
        deferral_creation: dispatchComplaint.deferralDate,
        mel_cdl_ref: melCdlRef,
        category: dispatchComplaint.category,
        mel_reference: isDdDeferral ? '' : dispatchComplaint.quickReference,
        document_reference: documentReference,
        number_installed: isDdDeferral ? '' : dispatchComplaint.numberInstalled,
        number_required: isDdDeferral ? '' : dispatchComplaint.numberRequired,
        placard_required: isDdDeferral ? null : dispatchComplaint.placardRequired,
        installed_required: isDdDeferral ? '' : dispatchComplaint.installedRequired,
        to_go_days: dispatchComplaint.toGoDays
      };
      const res = await updateComplaint(selectedComplaintId, {
        status: 'DEFERRED',
        section: deferredSection,
        mcc_reference: documentReference || melCdlRef || null,
        due_at: dueDate.toISOString().slice(0, 19).replace('T', ' '),
        due_hours: Number.isFinite(dueHours) ? dueHours * 24 : null,
        deferral_category: deferralPayload.deferral_category,
        deferral_type: deferralPayload.deferral_type,
        deferral_station: deferralPayload.deferral_station,
        deferral_reason: deferralPayload.deferral_reason,
        deferral_date: deferralPayload.deferral_date,
        deferral_creation: deferralPayload.deferral_creation,
        mel_cdl_ref: deferralPayload.mel_cdl_ref,
        action_type: 'DISPATCH',
        action_text: dispatchActionText,
        action_metadata: {
          category: deferralCategory || dispatchComplaint.category,
          mel_reference: deferralPayload.mel_reference,
          document_reference: deferralPayload.document_reference,
          deferral_type: deferralType,
          number_installed: deferralPayload.number_installed,
          number_required: deferralPayload.number_required,
          placard_required: deferralPayload.placard_required,
          installed_required: deferralPayload.installed_required,
          transfer_baseline: dispatchComplaint.transferDate,
          transfer_fh: dispatchComplaint.transferFh,
          transfer_fc: dispatchComplaint.transferFc,
          to_go_days: dispatchComplaint.toGoDays,
          to_go_fh: dispatchComplaint.toGoFh,
          to_go_fc: dispatchComplaint.toGoFc,
          due_at_date: dispatchComplaint.dueAtDate,
          due_at_fh: dispatchComplaint.dueAtFh,
          due_at_fc: dispatchComplaint.dueAtFc,
          deferral_reason: dispatchComplaint.reason,
          deferral_station: dispatchComplaint.deferralStation,
          deferral_date: dispatchComplaint.deferralDate,
          repetitive_inspection: false,
          repetitive_inspection_description: '',
          inspection_interval_days: '',
          inspection_interval_fh: '',
          inspection_interval_fc: ''
        }
      });
      cacheComplaintDeferral(selectedComplaintId, deferralPayload);
      const savedComplaint = res.data.complaint || { ...selectedComplaint, id: selectedComplaintId };
      setSelectedComplaint(savedComplaint);
      const complaintsRes = await getComplaints(aircraft.id);
      setComplaints(complaintsRes.data.complaints || []);
      setAircraftStatusInitialComplaint(savedComplaint);
      setAircraftStatusReturnView('main');
      setViewTransitionDirection('back');
      setView('aircraftStatus');
    } catch (err) {
      console.error(err);
    } finally {
      endComplaintAction();
    }
  };

  const handleCloseWorkorder = async () => {
    if (!selectedComplaintId) return;
    if (isComplaintLocked(selectedComplaint) && !isAdmin) return;
    if (!window.confirm('Close this workorder?')) return;
    if (!beginComplaintAction()) return;
    try {
      const res = await updateComplaint(selectedComplaintId, {
        status: 'CLOSED',
        action_type: 'STATUS',
        action_text: 'WORKORDER CLOSED.'
      });
      setSelectedComplaint(res.data.complaint || null);
      await loadComplaintActions(selectedComplaintId);
      const complaintsRes = await getComplaints(aircraft.id);
      setComplaints(complaintsRes.data.complaints || []);
    } catch (err) {
      console.error(err);
    } finally {
      endComplaintAction();
    }
  };

  const handleAccept = async (signatureDataUrl = null) => {
    setSaving(true);
    try {
      const nextCrew = getCrewForSave();
      if (nextCrew.length > 0) {
        await saveCrew(flightId, nextCrew);
        setCrew(nextCrew);
      }
      const res = await acceptAircraft(flightId, { signature_data_url: signatureDataUrl });
      setAcceptModalOpen(false);
      if (onAccepted) {
        onAccepted(res.data?.flight || null);
      } else {
        onBack();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setCancelModalOpen(true);
  };

  const confirmCancelFlight = async () => {
    setSaving(true);
    try {
      await discardFlight(flightId);
      setCancelModalOpen(false);
      if (onFlightEnded) {
        onFlightEnded();
      } else {
        onBack();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={viewContainerClass}>
        <div className="spinner" style={{ margin: 'auto' }} />
      </div>
    );
  }

  const f = flight?.flight;
  const flightInfoFallback = allowInitialFlightFallback
    ? {
        ...(getCreatedFlightPayload(flightId) || {}),
        ...(initialFlight || {}),
        ...(f || {})
      }
    : { ...(f || {}) };
  const flightDataDateValue = flightInfoFallback.date_of_flight
    ? formatDisplayDate(flightInfoFallback.date_of_flight)
    : format(new Date(), 'dd.MMM.yyyy');
  const flightDataFlightNumberValue = flightData.flight_number || flightInfoFallback.flight_number || '';
  const flightDataFromValue = flightData.from_airport || flightInfoFallback.from_airport || '';
  const flightDataToValue = flightData.to_airport || flightInfoFallback.to_airport || '';
  const formatFlightDataFlightNumber = (value) =>
    value.toUpperCase().replace(/\s+/g, '').replace(/[^A-Z0-9]/g, '').slice(0, 8);
  const getSimilarComplaintFilterValue = (complaint, fieldId) => {
    switch (fieldId) {
      case 'work_order_number':
        return `${complaint.work_order_number || ''} ${complaint.id || ''}`;
      case 'section':
        return complaint.section || '';
      case 'status':
        return complaint.status || '';
      case 'mcc_reference':
        return complaint.mcc_reference || '';
      case 'ata_chapter':
        return complaint.ata_chapter || '';
      case 'type':
        return `${complaint.type || ''} ${complaint.category || ''}`;
      default:
        return '';
    }
  };
  const getOooIInputClass = (canEdit, value, required = true) => {
    if (!canEdit) return 'fd-time-input-wrap fd-time-disabled';
    if (!required) return 'fd-time-input-wrap fd-time-active fd-time-optional';
    return `fd-time-input-wrap fd-time-active ${value ? 'complete' : 'incomplete'}`;
  };

  const handleSimilarComplaintCalendarToggle = (fieldKey) => {
    setSimilarComplaintFilterOpen(false);
    setSimilarComplaintOpenCalendar(current => current === fieldKey ? null : fieldKey);
    setSimilarComplaintCalendarMonths(prev => ({
      ...prev,
      [fieldKey]: fieldKey === 'from' && similarComplaintDateFrom
        ? parseISO(similarComplaintDateFrom)
        : fieldKey === 'to' && similarComplaintDateTo
          ? parseISO(similarComplaintDateTo)
          : prev[fieldKey]
    }));
  };

  const handleSimilarComplaintCalendarMonthChange = (fieldKey, direction) => {
    setSimilarComplaintCalendarMonths(prev => ({
      ...prev,
      [fieldKey]: direction > 0 ? addMonths(prev[fieldKey], 1) : subMonths(prev[fieldKey], 1)
    }));
  };

  const handleSimilarComplaintCalendarSelect = (fieldKey, day) => {
    const nextValue = format(day, 'yyyy-MM-dd');
    if (fieldKey === 'from') {
      setSimilarComplaintDateFrom(nextValue);
      if (similarComplaintDateTo && similarComplaintDateTo < nextValue) {
        setSimilarComplaintDateTo(nextValue);
      }
    } else {
      setSimilarComplaintDateTo(nextValue);
      if (similarComplaintDateFrom && similarComplaintDateFrom > nextValue) {
        setSimilarComplaintDateFrom(nextValue);
      }
    }
    setSimilarComplaintOpenCalendar(null);
  };

  const handleSimilarComplaintCalendarClear = (fieldKey) => {
    if (fieldKey === 'from') setSimilarComplaintDateFrom('');
    if (fieldKey === 'to') setSimilarComplaintDateTo('');
    setSimilarComplaintOpenCalendar(null);
  };

  const similarComplaintOptionCounts = (
    SIMILAR_COMPLAINT_FILTER_OPTIONS.reduce((acc, option) => {
      acc[option.id] = complaints.filter(item => String(getSimilarComplaintFilterValue(item, option.id)).trim()).length;
      return acc;
    }, {})
  );

  const similarComplaints = (() => {
    const queryWords = String(newComplaint.defect_description || '')
      .toLowerCase()
      .split(/\s+/)
      .map(word => word.trim())
      .filter(word => word.length >= 3);

    const scored = complaints.map(item => {
      let score = 0;
      if (newComplaint.ata_chapter && item.ata_chapter === newComplaint.ata_chapter) score += 4;
      if (newComplaint.type && ((item.type || item.category) === newComplaint.type)) score += 2;
      if (queryWords.length) {
        const description = String(item.defect_description || '').toLowerCase();
        queryWords.forEach(word => {
          if (description.includes(word)) score += 1;
        });
      }
      return { item, score };
    });

    return scored
      .filter(entry => entry.score > 0 || (!newComplaint.ata_chapter && queryWords.length === 0))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const priority = item => {
          const status = String(item.status || '').toUpperCase();
          if (status === 'DEFERRED') return 0;
          if (status === 'CLOSED') return 2;
          return 1;
        };
        const priorityDelta = priority(a.item) - priority(b.item);
        if (priorityDelta !== 0) return priorityDelta;
        return new Date(b.item.updated_at || b.item.opened_at || 0) - new Date(a.item.updated_at || a.item.opened_at || 0);
      })
      .map(entry => entry.item)
      .filter(item => {
        const filterValue = String(getSimilarComplaintFilterValue(item, similarComplaintFilterField)).toLowerCase();
        const matchesSearch = !similarComplaintSearch.trim() || filterValue.includes(similarComplaintSearch.trim().toLowerCase());
        const dateKey = formatDateSafe(item.opened_at || item.closed_at || item.updated_at, 'yyyy-MM-dd');
        const matchesFrom = !similarComplaintDateFrom || (dateKey && dateKey >= similarComplaintDateFrom);
        const matchesTo = !similarComplaintDateTo || (dateKey && dateKey <= similarComplaintDateTo);
        return matchesSearch && matchesFrom && matchesTo;
      })
      .slice(0, 8);
  })();

  const renderCreateComplaintPage = (containerClassName = viewContainerClass) => (
    <div className={containerClassName}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />ON GROUND</button>
        <div className="ogm-page-title">Create New Complaint</div>
        <button className="ogm-save-btn" onClick={handleSaveComplaint} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        <div className="complaint-create-card">
          <div className="fd-grid">
            <div className="fd-label">Type</div>
            <div className="fuel-unit-toggle fuel-v2-unit-toggle complaint-type-toggle">
              <button type="button" className={`toggle-option ${newComplaint.type === 'Pilot' ? 'active' : ''}`}
                onClick={() => setNewComplaint(c => ({ ...c, type: 'Pilot' }))}>Pilot</button>
              <button type="button" className={`toggle-option ${newComplaint.type === 'Cabin' ? 'active' : ''}`}
                onClick={() => setNewComplaint(c => ({ ...c, type: 'Cabin' }))}>Cabin</button>
            </div>
            <div className="fd-label">ATA Chapter</div>
            <select className="form-select ui-dropdown" value={newComplaint.ata_chapter}
              onChange={e => setNewComplaint(c => ({ ...c, ata_chapter: e.target.value }))}>
              <option value="">Select ATA Chapter</option>
              {ataChapters.map(a => (
                <option key={a.id} value={a.chapter}>{a.chapter} – {a.description}</option>
              ))}
            </select>
            <div className="fd-label">Defect Description</div>
            <textarea className="form-textarea complaint-textarea" rows="4" value={newComplaint.defect_description}
              onChange={e => setNewComplaint(c => ({ ...c, defect_description: e.target.value }))}
              placeholder="Describe the defect..." />
          </div>
        </div>

        <div className="complaint-similar-section">
          <h3 className="ogm-section-label">Similar Complaints</h3>
          <div className="complaint-similar-toolbar">
            <div className="complaint-similar-filter-row">
              <span className="complaint-similar-label">Filter</span>
              <div className="complaint-similar-filter-dropdown" ref={similarComplaintFilterRef}>
                <button
                  type="button"
                  className="complaint-similar-filter-trigger"
                  onClick={() => setSimilarComplaintFilterOpen(open => !open)}
                >
                  <span>{SIMILAR_COMPLAINT_FILTER_OPTIONS.find(option => option.id === similarComplaintFilterField)?.label || 'Filter'}</span>
                  <span className={`complaint-similar-filter-chevron ${similarComplaintFilterOpen ? 'open' : ''}`}>▾</span>
                </button>
                <div className={`complaint-similar-filter-menu ${similarComplaintFilterOpen ? 'open' : ''}`}>
                  {SIMILAR_COMPLAINT_FILTER_OPTIONS.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      className={`complaint-similar-filter-option ${similarComplaintFilterField === option.id ? 'active' : ''}`}
                      onClick={() => {
                        setSimilarComplaintFilterField(option.id);
                        setSimilarComplaintFilterOpen(false);
                      }}
                    >
                      <span>{option.label}</span>
                      <span>{similarComplaintOptionCounts[option.id] || 0}</span>
                    </button>
                  ))}
                </div>
              </div>
              <input
                type="text"
                className="complaint-similar-search"
                value={similarComplaintSearch}
                onChange={e => setSimilarComplaintSearch(e.target.value)}
                placeholder="Search for..."
              />
            </div>

            <div className="complaint-similar-date-row" ref={similarComplaintCalendarRef}>
              <span className="complaint-similar-label">Date</span>
              <SimilarComplaintCalendarField
                fieldKey="from"
                label="From"
                value={similarComplaintDateFrom}
                isOpen={similarComplaintOpenCalendar === 'from'}
                visibleMonth={similarComplaintCalendarMonths.from}
                onToggle={handleSimilarComplaintCalendarToggle}
                onChangeMonth={handleSimilarComplaintCalendarMonthChange}
                onSelectDate={handleSimilarComplaintCalendarSelect}
                onClear={handleSimilarComplaintCalendarClear}
              />
              <span className="complaint-similar-date-sep">-</span>
              <SimilarComplaintCalendarField
                fieldKey="to"
                label="To"
                value={similarComplaintDateTo}
                isOpen={similarComplaintOpenCalendar === 'to'}
                visibleMonth={similarComplaintCalendarMonths.to}
                onToggle={handleSimilarComplaintCalendarToggle}
                onChangeMonth={handleSimilarComplaintCalendarMonthChange}
                onSelectDate={handleSimilarComplaintCalendarSelect}
                onClear={handleSimilarComplaintCalendarClear}
              />
            </div>
          </div>

          <div className="complaint-similar-results">
            {similarComplaints.length ? similarComplaints.map(c => (
              <button key={c.id} type="button" className={`mcc-complaint-item complaint-similar-item ${getComplaintLineClass(c)}`} onClick={() => openComplaintDetail(c.id)}>
                <div className="mcc-complaint-main">
                  <div className="mcc-complaint-top">
                    <span className="mcc-complaint-wo">{c.work_order_number || `WO.${c.id}`}</span>
                    <span className={`mcc-complaint-badge ${getComplaintStatusBadgeClass(c)}`}>{getComplaintStatusBadgeLabel(c)}</span>
                    <span className="mcc-complaint-badge mcc-complaint-badge-muted">{c.type || c.category || 'Pilot'}</span>
                  </div>
                  <div className="mcc-complaint-desc">{c.defect_description || 'No description provided'}</div>
                  <div className="mcc-complaint-meta">
                    <div className="mcc-complaint-meta-item">
                      <span className="mcc-complaint-meta-label">Opened</span>
                      <span className="mcc-complaint-meta-value">{formatDisplayDate(c.opened_at)}</span>
                    </div>
                    {String(c.status || '').toUpperCase() === 'CLOSED' ? (
                      <div className="mcc-complaint-meta-item">
                        <span className="mcc-complaint-meta-label">Closed</span>
                        <span className="mcc-complaint-meta-value">{formatDisplayDate(c.closed_at || c.updated_at)}</span>
                      </div>
                    ) : String(c.status || '').toUpperCase() === 'DEFERRED' ? (
                      <div className="mcc-complaint-meta-item">
                        <span className="mcc-complaint-meta-label">Deferred</span>
                        <span className="mcc-complaint-meta-value">{formatDisplayDate(c.deferral_date || c.updated_at)}</span>
                      </div>
                    ) : (
                      <div className="mcc-complaint-meta-item">
                        <span className="mcc-complaint-meta-label">Due at</span>
                        <span className="mcc-complaint-meta-value">{c.due_at ? formatDisplayDate(c.due_at) : '---'}</span>
                      </div>
                    )}
                  </div>
                </div>
                <Chevron />
              </button>
            )) : (
              <div className="complaint-similar-empty">No similar complaints found.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderComplaintDetailPage = (containerClassName = viewContainerClass) => {
    const latestDispatchAction = [...complaintActions].reverse().find(action => action.action_type === 'DISPATCH');
    const latestDispatchMeta = parseActionMetadata(latestDispatchAction?.metadata_json);
    const liveToGoDays = formatRemainingToGoDays(selectedComplaint?.due_at, toGoNow);
    const storedDueHours = Number(selectedComplaint?.due_hours);
    const storedDueDays = Number.isFinite(storedDueHours) && storedDueHours > 0 ? `${Math.round(storedDueHours / 24)} Days` : '';
    const detailToGoDays = liveToGoDays || (latestDispatchMeta.to_go_days ? `${latestDispatchMeta.to_go_days} Days` : '---');
    const detailDefValue = latestDispatchMeta.to_go_days ? `${latestDispatchMeta.to_go_days} Days` : (storedDueDays || '---');
    const isClosed = selectedComplaint?.status === 'CLOSED';
    const isDeferred = selectedComplaint?.status === 'DEFERRED';
    const complaintLocked = isComplaintLocked(selectedComplaint);
    const canEditComplaint = !complaintLocked || isAdmin;
    const hasBeenDispatched = isDeferred || selectedComplaint?.section === 'airworthy_deferred' || complaintActions.some(action => action.action_type === 'DISPATCH');
    const showInitialActions = selectedComplaint && !isClosed && !hasBeenDispatched && canEditComplaint;
    const showDeferredActions = selectedComplaint && !isClosed && hasBeenDispatched && canEditComplaint;
    const hasVoidAction = complaintActions.some(action => action.action_type === 'VOID' || String(action.action_text || '').toLowerCase().includes('void'));
    const showFallbackVoidAction = selectedComplaint?.section === 'voided' && !hasVoidAction;

    const renderActionDetails = (action, index) => {
      const metadata = parseActionMetadata(action.metadata_json);
      const isVoidAction = action.action_type === 'VOID' || String(action.action_text || '').toLowerCase().includes('void');
      const isActionEntry = action.action_type === 'ACTION';
      const intervalParts = [
        metadata.inspection_interval_days ? `${metadata.inspection_interval_days} DAY(S)` : '',
        metadata.inspection_interval_fh ? `${metadata.inspection_interval_fh} FH` : '',
        metadata.inspection_interval_fc ? `${metadata.inspection_interval_fc} FC` : ''
      ].filter(Boolean);

      if (isVoidAction) {
        return (
          <div className="complaint-update-card complaint-update-card-action" key={action.id || index}>
            <div className="complaint-update-head">
              <span>Action {index + 1}</span>
              <span>{formatDisplayDateTime(action.performed_at)}</span>
            </div>
            <div className="complaint-update-text">{metadata.action || action.action_text || VOID_ACTION_TEXT}</div>
          </div>
        );
      }

      return (
        <div className="complaint-update-card" key={action.id || index}>
          <div className="complaint-update-head">
            <span>{action.action_type === 'STATUS' ? 'Status Update' : `Action ${index + 1}`}</span>
            <span>{formatDisplayDateTime(action.performed_at)}</span>
          </div>
          <div className={`complaint-update-text ${isActionEntry ? 'complaint-update-text-normal' : ''}`}>{action.action_text || 'Complaint updated.'}</div>
          {metadata.repetitive_inspection ? (
            <>
              <div className="complaint-update-text">REPETITIVE INSPECTION CREATED:</div>
              <div className="complaint-update-text complaint-update-text-normal">{metadata.repetitive_inspection_description || 'DO INSPECTION'}</div>
              {intervalParts.length ? <div className="complaint-update-text">REPETITIVE INSPECTION INTERVALS: {intervalParts.join(' / ')}</div> : null}
            </>
          ) : null}
          {metadata.deferral_station ? <div className="complaint-update-text">DEFERRAL STATION: {metadata.deferral_station}</div> : null}
          {action.performed_by_name ? <div className="complaint-update-text">PERFORMED SIGN: {action.performed_by_name}</div> : null}
        </div>
      );
    };
    const complaintHistoryCards = [];
    let actionCardNumber = 0;
    let hasOpenedStatusUpdate = false;

    complaintActions.forEach((action, index) => {
      const statusUpdateText = getWorkorderStatusUpdateText(action);
      if (statusUpdateText) {
        if (statusUpdateText === 'WORKORDER OPENED.') hasOpenedStatusUpdate = true;
        complaintHistoryCards.push(
          <div className="complaint-update-card" key={`status-${action.id || index}`}>
            <div className="complaint-update-head">
              <span>Status Update</span>
              <span>{formatDisplayDateTime(action.performed_at)}</span>
            </div>
            <div className="complaint-update-text">{statusUpdateText}</div>
          </div>
        );
      }

      if (shouldRenderWorkorderActionCard(action)) {
        actionCardNumber += 1;
        complaintHistoryCards.push(renderActionDetails(action, actionCardNumber - 1));
      }
    });

    if (selectedComplaint && !hasOpenedStatusUpdate) {
      complaintHistoryCards.unshift(
        <div className="complaint-update-card" key="status-opened-fallback">
          <div className="complaint-update-head">
            <span>Status Update</span>
            <span>{formatDisplayDateTime(selectedComplaint.opened_at)}</span>
          </div>
          <div className="complaint-update-text">WORKORDER OPENED.</div>
        </div>
      );
    }

    if (showFallbackVoidAction) {
      actionCardNumber += 1;
      complaintHistoryCards.push(
        <div className="complaint-update-card complaint-update-card-action" key="void-fallback">
          <div className="complaint-update-head">
            <span>Action {actionCardNumber}</span>
            <span>{formatDisplayDateTime(selectedComplaint.closed_at || selectedComplaint.updated_at)}</span>
          </div>
          <div className="complaint-update-text">{selectedComplaint.mcc_reference || VOID_ACTION_TEXT}</div>
        </div>
      );
    }

    return (
      <div className={containerClassName}>
        <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackFromComplaintDetail}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />{complaintDetailReturnView === 'createComplaint' ? 'CREATE COMPLAINT' : 'ON GROUND'}</button>
          <div className="ogm-page-title">Workorder Detail</div>
          <button className="ogm-save-btn" onClick={reloadComplaintDetail} disabled={saving}>
            {saving ? 'Reloading...' : 'Reload'}
          </button>
        </div>
        <div className="ogm-scroll-area complaint-detail-scroll">
          {selectedComplaint ? (
            <>
              <div className={`complaint-detail-card complaint-detail-card-status-${String(selectedComplaint.status || 'open').toLowerCase()} ${getComplaintLineClass(selectedComplaint)}`}>
                <div className="complaint-detail-top">
                  <div className="complaint-detail-title-group">
                    <div className="complaint-detail-title-line">
                      <div className="complaint-detail-wo">{selectedComplaint.work_order_number || `WO.${selectedComplaint.id}`}</div>
                      <div className="complaint-detail-badges">
                        <span className={`complaint-detail-badge ${getComplaintStatusBadgeClass(selectedComplaint)}`}>{getComplaintStatusBadgeLabel(selectedComplaint)}</span>
                        <span className="complaint-detail-badge complaint-detail-badge-muted">{selectedComplaint.type || selectedComplaint.category || 'Pilot'}</span>
                        {complaintLocked && isAdmin ? <span className="complaint-detail-badge complaint-detail-badge-locked">Locked</span> : null}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="complaint-detail-desc">{selectedComplaint.defect_description || '---'}</div>
                <div className="complaint-detail-grid complaint-detail-primary-grid">
                  <div className="complaint-detail-pair">
                    <span className="complaint-detail-label">Opened</span>
                    <span className="complaint-detail-value">{formatDisplayDateTime(selectedComplaint.opened_at) || '---'}</span>
                  </div>
                  <div className="complaint-detail-pair">
                    <span className="complaint-detail-label">Due at</span>
                    <span className="complaint-detail-value">
                      {selectedComplaint.due_at ? `${formatDisplayDate(selectedComplaint.due_at)}${selectedComplaint.due_hours ? ` (${selectedComplaint.due_hours} H)` : ''}` : '---'}
                    </span>
                  </div>
                  <div className="complaint-detail-pair">
                    <span className="complaint-detail-label">Issue Station</span>
                    <span className="complaint-detail-value">{flight?.flight?.from_airport || latestDispatchMeta.deferral_station || '---'}</span>
                  </div>
                  <div className="complaint-detail-pair">
                    <span className="complaint-detail-label">To Go</span>
                    <span className="complaint-detail-value">{detailToGoDays}</span>
                  </div>
                  <div className="complaint-detail-pair">
                    <span className="complaint-detail-label">ATA</span>
                    <span className="complaint-detail-value">{selectedComplaint.ata_chapter || '---'}</span>
                  </div>
                  <div className="complaint-detail-pair">
                    <span className="complaint-detail-label">Def. Value</span>
                    <span className="complaint-detail-value">{detailDefValue}</span>
                  </div>
                </div>

                {hasBeenDispatched ? (
                  <div className="complaint-deferral-panel">
                    <div className="complaint-detail-pair">
                      <span className="complaint-detail-label">MEL Ref</span>
                      <span className="complaint-detail-value">{String(latestDispatchMeta.category || '').toUpperCase() === 'DD' ? '---' : (latestDispatchMeta.mel_reference || selectedComplaint.mcc_reference || 'reference')}</span>
                    </div>
                    <div className="complaint-detail-pair">
                      <span className="complaint-detail-label">Deferral Type</span>
                      <span className="complaint-detail-value">{latestDispatchMeta.deferral_type ? `${getDispatchDeferralTypeLabel(latestDispatchMeta.category)} ${latestDispatchMeta.deferral_type}` : '---'}</span>
                    </div>
                    <div className="complaint-detail-pair">
                      <span className="complaint-detail-label">Def. Station</span>
                      <span className="complaint-detail-value">{latestDispatchMeta.deferral_station || '---'}</span>
                    </div>
                    <div className="complaint-detail-pair">
                      <span className="complaint-detail-label">Def. Reason</span>
                      <span className="complaint-detail-value">{latestDispatchMeta.deferral_reason || '---'}</span>
                    </div>
                    <div className="complaint-detail-pair">
                      <span className="complaint-detail-label">Def. Date</span>
                      <span className="complaint-detail-value">{latestDispatchMeta.deferral_date || formatDisplayDateTime(latestDispatchAction?.performed_at) || '---'}</span>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="complaint-detail-actions">
                {isAdmin ? (
                  <button type="button" className={`ogm-create-complaint-btn complaint-action-btn complaint-lock-btn ${complaintLocked ? 'is-locked' : ''}`} onClick={handleToggleComplaintLock} disabled={saving}>
                    {complaintLocked ? 'Unlock Editing' : 'Lock Editing'}
                  </button>
                ) : null}
                {showInitialActions ? (
                  <>
                    <button type="button" className="ogm-create-complaint-btn complaint-action-btn" onClick={() => setVoidComplaintModalOpen(true)} disabled={saving}>Void Complaint</button>
                    <button type="button" className="ogm-create-complaint-btn complaint-action-btn" onClick={() => openDispatchComplaintPage(selectedComplaint)} disabled={saving}>Dispatch Complaint</button>
                  </>
                ) : null}
                {showDeferredActions ? (
                  <>
                    <button type="button" className="ogm-create-complaint-btn complaint-action-btn" onClick={() => setVoidComplaintModalOpen(true)} disabled={saving}>Void Complaint</button>
                    <button type="button" className="ogm-create-complaint-btn complaint-action-btn" onClick={handleCloseWorkorder} disabled={saving}>Close Workorder</button>
                  </>
                ) : null}
              </div>

              <div className="complaint-updates-list">
                {complaintHistoryCards}
              </div>
            </>
          ) : (
            <div className="complaint-detail-empty">No complaint data available.</div>
          )}
        </div>
      </div>
    );
  };

  const renderMelBrowserPage = () => {
    const chapter = melTree.find(node => node.code === melPath.chapter);
    const section = chapter?.sections.find(node => node.code === melPath.section);
    const subsection = section?.subsections.find(node => node.code === melPath.subsection);
    const baseContainer = subsection || section;
    const base = baseContainer?.bases.find(node => node.code === melPath.base);
    const isSearching = melSearch.trim().length > 0;
    const currentGroups = !isSearching
      ? (!chapter ? melTree : !section ? chapter.sections : !subsection && section.subsections.length ? section.subsections : !base ? (baseContainer?.bases || []) : [])
      : [];
    const currentItems = isSearching ? melSearchResults : (base?.items || []);
    const currentBreadcrumb = isSearching
      ? { label: 'ATA - Main Categories', level: 'root' }
      : melPreviewItem
        ? { label: `${melPreviewItem.base_number} - ${melPreviewItem.base_title || melPreviewItem.title}`, level: 'base' }
      : base
        ? { label: `${base.code} - ${base.title}`, level: 'subsection' }
      : subsection
        ? { label: `${subsection.code} - ${subsection.title}`, level: 'section' }
        : section
          ? { label: `${section.code} - ${section.title}`, level: 'chapter' }
          : chapter
          ? { label: `${chapter.code} - ${chapter.title}`, level: 'root' }
            : { label: 'ATA - Main Categories', level: 'root' };
    const melApplicabilityItems = formatMelApplicability(melPreviewItem?.applicability);
    const libraryLabel = dispatchLibrary;

    const setMelLevel = (node) => {
      setMelPreviewItem(null);
      if (!chapter) {
        setMelPath({ chapter: node.code, section: '', subsection: '', base: '' });
      } else if (!section) {
        setMelPath(prev => ({ ...prev, section: node.code, subsection: '', base: '' }));
      } else if (!subsection && section.subsections.length) {
        setMelPath(prev => ({ ...prev, subsection: node.code, base: '' }));
      } else {
        setMelPath(prev => ({ ...prev, base: node.code }));
      }
    };

    const navigateMelCrumb = (level) => {
      setMelPreviewItem(null);
      if (level === 'root') setMelPath({ chapter: '', section: '', subsection: '', base: '' });
      if (level === 'chapter') setMelPath(prev => ({ ...prev, section: '', subsection: '', base: '' }));
      if (level === 'section') setMelPath(prev => ({ ...prev, subsection: '', base: '' }));
      if (level === 'subsection') setMelPath(prev => ({ ...prev, base: '' }));
      if (level === 'base') setMelPreviewItem(null);
    };

    return (
      <div className={viewContainerClass}>
        <div className="ogm-sub-header">
          <button className="ogm-back-btn" onClick={() => { setViewTransitionDirection('back'); setView('dispatchComplaint'); }}>
          <img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />DISPATCH COMPLAINT
          </button>
          <div className="ogm-page-title">{libraryLabel} Database</div>
          <button className="ogm-save-btn" disabled>Search</button>
        </div>

        <div className="ogm-scroll-area mel-browser-page">
          <div className="mel-browser-search-card">
            <label className="mel-browser-label">Search by {libraryLabel} number or word</label>
            <input
              className="form-input mel-browser-search"
              value={melSearch}
              onChange={e => {
                setMelPreviewItem(null);
                setMelSearch(e.target.value);
              }}
              placeholder={`Search ${libraryLabel}...`}
            />
          </div>

          <div className="mel-browser-breadcrumbs">
            <button
              type="button"
              className="active"
              onClick={() => navigateMelCrumb(currentBreadcrumb.level)}
              disabled={isSearching || (!chapter && !section && !subsection && !base)}
            >
              {!isSearching && (chapter || section || subsection || base) && <img src={arrowLeftIcon} alt="" className="mel-crumb-arrow app-icon app-icon-arrow-sm-left" />}
              <span className="mel-crumb-code">{currentBreadcrumb.label}</span>
            </button>
          </div>

          {melItemsLoading ? (
            <div className="mel-empty-card">Loading {libraryLabel} database...</div>
          ) : melPreviewItem ? (
            <div className="mel-detail-card">
              <div className="mel-detail-header">
                <div>
                  <div className="mel-result-reference">{melPreviewItem.reference}</div>
                  <div className="mel-detail-title">{melPreviewItem.title || melPreviewItem.base_title}</div>
                </div>
                <button type="button" className="mel-detail-select-btn" onClick={() => selectMelItem(melPreviewItem)}>Select {libraryLabel}</button>
              </div>
              <div className="mel-result-meta">
                <span>{melPreviewItem.ata_chapter} {melPreviewItem.ata_chapter_title}</span>
                <span>{melPreviewItem.ata_section} {melPreviewItem.ata_section_title}</span>
                {melPreviewItem.ata_subsection && <span>{melPreviewItem.ata_subsection} {melPreviewItem.ata_subsection_title}</span>}
              </div>
              {melPreviewItem.applicability && (
                <div className="mel-detail-section">
                  <span>Applicable to</span>
                  <div className="mel-applicability-list">
                    {melApplicabilityItems.length > 1 ? (
                      melApplicabilityItems.map(item => <span key={item} className="mel-applicability-chip">{item}</span>)
                    ) : (
                      <p>{melPreviewItem.applicability}</p>
                    )}
                  </div>
                </div>
              )}
              {renderMelRequirementTable(melPreviewItem)}
              {melPreviewItem.remarks && (
                <div className="mel-detail-section">
                  <span>Details</span>
                  {renderMelDetailText(melPreviewItem.remarks)}
                </div>
              )}
            </div>
          ) : isSearching ? (
            <div className="mel-browser-results">
              {melSearchResults.length === 0 ? (
                <div className="mel-empty-card">No {libraryLabel} items found.</div>
              ) : melSearchResults.map((item, index) => (
                <button key={getMelItemKey(item, index)} type="button" className="mel-result-card" onClick={() => setMelPreviewItem(item)}>
                  <div className="mel-result-main">
                    <span className="mel-result-reference">{item.reference}</span>
                    <span className="mel-result-title">{item.title || item.base_title}</span>
                  </div>
                  <div className="mel-result-meta">
                    <span>{item.ata_chapter} {item.ata_chapter_title}</span>
                    <span>{libraryLabel} {item.mel_type || item.repair_interval || '-'}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : currentItems.length > 0 ? (
            <div className="mel-browser-results">
              {currentItems.map((item, index) => (
                <button key={getMelItemKey(item, index)} type="button" className="mel-result-card" onClick={() => setMelPreviewItem(item)}>
                  <div className="mel-result-main">
                    <span className="mel-result-reference">{item.reference}</span>
                    <span className="mel-result-title">{item.title || item.base_title}</span>
                  </div>
                  <div className="mel-result-meta">
                    <span>{libraryLabel} {item.mel_type || item.repair_interval || '-'}</span>
                    <span>{item.placard_required === true ? 'Placard required' : item.placard_required === false ? 'No placard' : (item.repair_interval || libraryLabel)}</span>
                  </div>
                  {item.remarks && <div className="mel-result-remarks">{item.remarks}</div>}
                </button>
              ))}
            </div>
          ) : (
            <div className="mel-browser-grid">
              {currentGroups.map(node => (
                <button key={node.code} type="button" className="mel-node-card" onClick={() => setMelLevel(node)}>
                  <div className="mel-node-code">{node.code}</div>
                  <div className="mel-node-title">{node.title}</div>
                  <div className="mel-node-count">{node.count} items</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDispatchComplaintPage = () => {
    const isDdDispatch = String(dispatchComplaint.category || '').toUpperCase() === 'DD';
    const toGoEditable = dispatchComplaint.melType === 'A';
    const dispatchDocumentLabel = dispatchLibrary;

    return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={() => { setViewTransitionDirection('back'); setView('complaintDetail'); }}>
          <img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />COMPLAINT DETAIL
        </button>
        <div className="ogm-page-title">Dispatch Complaint</div>
        <button className="ogm-save-btn" onClick={handleSaveDispatchComplaint} disabled={saving || (!isDdDispatch && !dispatchComplaint.documentReference.trim())}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="ogm-scroll-area dispatch-complaint-page">
        {selectedComplaint ? (
          <div className={`complaint-detail-card dispatch-associated-card ${getComplaintLineClass(selectedComplaint)}`}>
            <div className="complaint-detail-top">
              <div className="complaint-detail-title-group">
                <div className="complaint-detail-wo">{selectedComplaint.work_order_number || `WO.${selectedComplaint.id}`}</div>
                <div className="complaint-detail-badges">
                  <span className={`complaint-detail-badge ${getComplaintStatusBadgeClass(selectedComplaint)}`}>{getComplaintStatusBadgeLabel(selectedComplaint)}</span>
                  <span className="complaint-detail-badge complaint-detail-badge-muted">{selectedComplaint.type || selectedComplaint.category || 'Pilot'}</span>
                </div>
              </div>
            </div>
            <div className="complaint-detail-desc">{selectedComplaint.defect_description || '---'}</div>
            <div className="complaint-detail-grid">
              <div className="complaint-detail-pair">
                <span className="complaint-detail-label">Opened</span>
                <span className="complaint-detail-value">{formatDisplayDate(selectedComplaint.opened_at)}</span>
              </div>
              <div className="complaint-detail-pair">
                <span className="complaint-detail-label">Due at</span>
                <span className="complaint-detail-value">{selectedComplaint.due_at ? formatDisplayDate(selectedComplaint.due_at) : '---'}</span>
              </div>
            </div>
          </div>
        ) : null}

        <div className="dispatch-card">
          <div className={`dispatch-grid ${isDdDispatch ? 'dispatch-dd-mode' : ''}`}>
            <div className="dispatch-label">Category</div>
            <div className="dispatch-segment dispatch-category-toggle">
              {['MEL', 'DD'].map(category => (
                <button
                  key={category}
                  type="button"
                  className={`dispatch-segment-option ${dispatchComplaint.category === category ? 'active' : ''}`}
                  onClick={() => updateDispatchComplaint({ category })}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="dispatch-label dispatch-dd-only">DD Category</div>
            <div className="dispatch-segment dispatch-dd-toggle dispatch-dd-only">
              {['A', 'D'].map(type => (
                <button key={type} type="button" className={`dispatch-segment-option ${dispatchComplaint.melType === type ? 'active' : ''}`} onClick={() => updateDispatchComplaint({ melType: type })}>
                  CAT {type}
                </button>
              ))}
            </div>

            <div className="dispatch-label dispatch-mel-only">Quick {dispatchDocumentLabel} Selection</div>
            <button type="button" className="dispatch-mel-picker dispatch-mel-only" onClick={openMelBrowser}>
              <span className={dispatchComplaint.quickReference ? 'has-value' : ''}>
                {dispatchComplaint.quickReference
                  ? getMelDisplayLabel({ reference: dispatchComplaint.quickReference, title: dispatchComplaint.defectDescription?.split(' / ').slice(1).join(' / ') })
                  : `Select ${dispatchDocumentLabel} reference`}
              </span>
              <span aria-hidden="true">☰</span>
            </button>

            {dispatchComplaint.quickReference ? (
              <>
                <div className="dispatch-label dispatch-mel-only">{dispatchDocumentLabel} Item Limits</div>
                <div className="dispatch-mel-requirements dispatch-mel-only">
                  {renderMelRequirementTable(dispatchComplaint)}
                </div>
              </>
            ) : null}

            <div className="dispatch-label dispatch-mel-only">Deferral Type</div>
            <div className="dispatch-segment dispatch-mel-toggle dispatch-mel-only">
              {['A', 'B', 'C', 'D'].map(type => (
                <button
                  key={type}
                  type="button"
                  className={`dispatch-segment-option ${dispatchComplaint.melType === type ? 'active' : ''}`}
                  onClick={() => updateDispatchComplaint({ melType: type })}
                >
                  {type}
                </button>
              ))}
            </div>

            <div className="dispatch-label dispatch-mel-only">{dispatchDocumentLabel} Reference / Revision</div>
            <div className={`fuel-cell-input dispatch-required dispatch-mel-only ${dispatchComplaint.documentReference.trim() ? 'valid' : 'invalid'}`}>
              <input
                className="form-input dispatch-input dispatch-doc-input"
                value={dispatchComplaint.documentReference}
                onChange={e => updateDispatchComplaint({ documentReference: e.target.value })}
              />
            </div>

            <div className="dispatch-label">Transfer Baseline</div>
            <div className="dispatch-triple">
              <input className="form-input dispatch-input dispatch-readonly-input" value={dispatchComplaint.transferDate} readOnly />
              <div className="dispatch-suffix-wrap">
                <input className="form-input dispatch-input dispatch-readonly-input" value={dispatchComplaint.transferFh} readOnly />
                <span>FH</span>
              </div>
              <div className="dispatch-suffix-wrap">
                <input className="form-input dispatch-input dispatch-readonly-input" value={dispatchComplaint.transferFc} readOnly />
                <span>FC</span>
              </div>
            </div>

            <div className="dispatch-label dispatch-row-chip">To Go</div>
            <div className="dispatch-triple">
              <div className="dispatch-suffix-wrap">
                <input className={`form-input dispatch-input ${!toGoEditable ? 'dispatch-readonly-input' : ''}`} inputMode="numeric" value={dispatchComplaint.toGoDays} readOnly={!toGoEditable} onChange={e => updateDispatchComplaint({ toGoDays: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
                <span>Days</span>
              </div>
              <div className="dispatch-suffix-wrap">
                <input className={`form-input dispatch-input ${!toGoEditable ? 'dispatch-readonly-input' : ''}`} value={dispatchComplaint.toGoFh} readOnly={!toGoEditable} onChange={e => updateDispatchComplaint({ toGoFh: e.target.value })} />
                <span>FH</span>
              </div>
              <div className="dispatch-suffix-wrap">
                <input className={`form-input dispatch-input ${!toGoEditable ? 'dispatch-readonly-input' : ''}`} value={dispatchComplaint.toGoFc} readOnly={!toGoEditable} onChange={e => updateDispatchComplaint({ toGoFc: e.target.value })} />
                <span>FC</span>
              </div>
            </div>

            <div className="dispatch-label dispatch-row-chip">Due At</div>
            <div className="dispatch-triple">
              <input className="form-input dispatch-input dispatch-readonly-input" value={dispatchComplaint.dueAtDate} readOnly />
              <div className="dispatch-suffix-wrap">
                <input className="form-input dispatch-input dispatch-readonly-input" value={dispatchComplaint.dueAtFh} readOnly />
                <span>FH</span>
              </div>
              <div className="dispatch-suffix-wrap">
                <input className="form-input dispatch-input dispatch-readonly-input" value={dispatchComplaint.dueAtFc} readOnly />
                <span>FC</span>
              </div>
            </div>

            <div className="dispatch-label">Reason</div>
            <select className="form-select ui-dropdown dispatch-input" value={dispatchComplaint.reason} onChange={e => updateDispatchComplaint({ reason: e.target.value })}>
              {DISPATCH_REASONS.map(reason => <option key={reason} value={reason}>{reason}</option>)}
            </select>

            <div className="dispatch-label">Action Text</div>
            <textarea className="form-textarea dispatch-textarea" rows="3" value={dispatchComplaint.actionText} onChange={e => updateDispatchComplaint({ actionText: e.target.value })} />

            <div className="dispatch-label">Deferral Station / Date</div>
            <div className="dispatch-deferral-row">
              <input className="form-input dispatch-input" value={dispatchComplaint.deferralStation} onChange={e => updateDispatchComplaint({ deferralStation: e.target.value.toUpperCase().slice(0, 4) })} />
              <input className="form-input dispatch-input" value={dispatchComplaint.deferralDate} onChange={e => updateDispatchComplaint({ deferralDate: e.target.value })} />
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderVoidComplaintModal = () => createPortal((
    <div className="void-complaint-overlay" role="presentation" onMouseDown={() => !saving && setVoidComplaintModalOpen(false)}>
      <div className="void-complaint-modal" role="dialog" aria-modal="true" onMouseDown={e => e.stopPropagation()}>
        <div className="void-complaint-header">
          <span>Confirm Void Complaint</span>
        </div>
        <div className="void-complaint-body">
          You are about to void the complaint.
        </div>
        <div className="void-complaint-footer">
          <button type="button" className="void-complaint-btn" onClick={() => setVoidComplaintModalOpen(false)} disabled={saving}>
            <span aria-hidden="true">⊘</span>
            <span>Cancel</span>
          </button>
          <button type="button" className="void-complaint-btn void-complaint-confirm" onClick={handleVoidComplaint} disabled={saving || !selectedComplaintId}>
            <span aria-hidden="true">✓</span>
            <span>{saving ? 'Saving...' : 'Confirm'}</span>
          </button>
        </div>
      </div>
    </div>
  ), document.body);

  /* ── SUB VIEWS ──────────────────────────────────────────── */

  // Handler for Zulu time input changes
  const handleZuluChange = (field, value) => {
    const formatted = handleUtcTimeInput(value);
    setZuluTimes(prev => ({ ...prev, [field]: formatted }));

    // Convert the UTC time label to a DB datetime string without applying local timezone offset.
    if (/^\d{2}:\d{2}$/.test(formatted)) {
      const hours = parseInt(formatted.slice(0, 2), 10);
      const minutes = parseInt(formatted.slice(3, 5), 10);
      const baseDate =
        toValidDate(flightData[field]) ||
        toValidDate(flightInfoFallback.date_of_flight) ||
        new Date();
      baseDate.setUTCHours(hours, minutes, 0, 0);
      setFlightData(d => ({ ...d, [field]: formatUtcSqlDateTime(baseDate) }));
    } else {
      setFlightData(d => ({ ...d, [field]: '' }));
    }
  };

  // Backspace at the end of HH:mm should delete the previous digit.
  const handleZuluKeyDown = (field, e) => {
    if (e.key !== 'Backspace') return;

    const current = zuluTimes[field] || '';
    const isCursorAtEnd = e.target.selectionStart === current.length && e.target.selectionEnd === current.length;
    if (!isCursorAtEnd) return;

    e.preventDefault();

    const digits = current.replace(/\D/g, '').slice(0, 4);
    const nextDigits = digits.slice(0, -1);
    const nextFormatted = handleUtcTimeInput(nextDigits);

    setZuluTimes(prev => ({ ...prev, [field]: nextFormatted }));
    setFlightData(d => ({ ...d, [field]: '' }));

    requestAnimationFrame(() => {
      e.target.setSelectionRange(nextFormatted.length, nextFormatted.length);
    });
  };

  const updateDeicingRecord = (index, patch) => {
    setDeicingRecords(prev => prev.map((record, idx) => (
      idx === index ? { ...record, ...patch } : record
    )));
  };

  const handleDeicingMinutesInput = (value) => value.replace(/\D/g, '').slice(0, 3);

  const handleDeicingNumericInput = (value) => {
    if (value === '') return '';
    if (value === '.') return '0.';
    if (!/^\d*\.?\d*$/.test(value)) return null;
    return value;
  };

  const handleDeicingZuluChange = (index, value) => {
    updateDeicingRecord(index, { start_utc: handleZuluTimeInput(value) });
  };

  const handleDeicingZuluKeyDown = (index, e) => {
    if (e.key !== 'Backspace') return;

    const current = deicingRecords[index]?.start_utc || '';
    const isCursorAtEnd = e.target.selectionStart === current.length && e.target.selectionEnd === current.length;
    if (!current.endsWith('Z') || !isCursorAtEnd) return;

    e.preventDefault();
    const digits = current.replace(/\D/g, '').slice(0, 4);
    const nextDigits = digits.slice(0, -1);
    const nextFormatted = handleZuluTimeInput(nextDigits);
    updateDeicingRecord(index, { start_utc: nextFormatted });
    requestAnimationFrame(() => {
      e.target.setSelectionRange(nextFormatted.length, nextFormatted.length);
    });
  };

  const addDeicingRecord = () => {
    setDeicingRecords(prev => [...prev, createEmptyDeicingRecord(prev.length + 1)]);
    setAnimateDeicingAddBtn(true);
    setTimeout(() => setAnimateDeicingAddBtn(false), 240);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollArea = document.querySelector('.ogm-scroll-area');
        scrollArea?.scrollTo({ top: scrollArea.scrollHeight, behavior: 'smooth' });
      });
    });
  };

  const removeDeicingRecord = (indexToRemove) => {
    if (removingDeicingIndex !== null) return;
    if (deicingRecords.length <= 1 || indexToRemove <= 0) return;
    setRemovingDeicingIndex(indexToRemove);
    setTimeout(() => {
      setDeicingRecords(prev => (
        prev
          .filter((_, idx) => idx !== indexToRemove)
          .map((record, idx) => ({ ...record, record_number: idx + 1 }))
      ));
      setRemovingDeicingIndex(null);
    }, ROW_EXIT_ANIMATION_MS);
  };

  const removeCrewMember = (indexToRemove) => {
    if (removingCrewIndex !== null) return;
    if (crew.length <= 1 || indexToRemove <= 0) return;
    setRemovingCrewIndex(indexToRemove);
    setTimeout(() => {
      setCrew(prev => prev.filter((_, idx) => idx !== indexToRemove));
      setRemovingCrewIndex(null);
    }, ROW_EXIT_ANIMATION_MS);
  };

  const handleRemoveDelay = async (delayId) => {
    if (removingDelayId !== null) return;
    setRemovingDelayId(delayId);
    await new Promise(resolve => setTimeout(resolve, ROW_EXIT_ANIMATION_MS));
    try {
      await removeDelay(flightId, delayId);
      await loadFlight();
    } catch (err) {
      console.error(err);
    }
    setRemovingDelayId(null);
  };

  const handleDelayMinutesInput = (value) => value.replace(/\D/g, '').slice(0, 3);
  const isDelayMinutesValid = delay.delay_minutes !== '' && Number(delay.delay_minutes) >= 0 && Number(delay.delay_minutes) <= 999;
  const selectedDelayOption = DELAY_CODE_OPTIONS.find(option => option.code === delay.delay_code);

  const handleAddCrewMember = () => {
    setCrew(c => [...c, { full_name: '', title: 'First Officer' }]);
    setAnimateCrewAddBtn(true);
    setTimeout(() => setAnimateCrewAddBtn(false), 240);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollArea = document.querySelector('.ogm-scroll-area');
        scrollArea?.scrollTo({ top: scrollArea.scrollHeight, behavior: 'smooth' });
      });
    });
  };

  if (view === 'flightData') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />ON GROUND</button>
        <div className="ogm-page-title">Flight Data</div>
        <button className="ogm-save-btn" onClick={handleSaveFlightData} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        {flightDataError ? <div className="fd-validation-message">{flightDataError}</div> : null}
        {/* Flight Info Section */}
        <div className="fd-section">
          <div className="fd-row">
            <div className="fd-label">Date of Flight</div>
            <input className="form-input fd fd-input" readOnly value={flightDataDateValue} />
          </div>
          <div className="fd-row">
            <div className="fd-label">Flight Number</div>
            <input
              className="form-input fd fd-input"
              value={flightDataFlightNumberValue}
              onChange={e => setFlightData(d => ({ ...d, flight_number: formatFlightDataFlightNumber(e.target.value) }))}
              maxLength={8}
            />
          </div>
          <div className="fd-row">
            <div className="fd-label">From - To</div>
            <div className="fd-from-to">
              <input
                className="form-input fd fd-input"
                value={flightDataFromValue}
                onChange={e => setFlightData(d => ({ ...d, from_airport: formatIcaoInput(e.target.value) }))}
                maxLength={4}
              />
              <span className="fd-separator">–</span>
              <input
                className="form-input fd fd-input"
                value={flightDataToValue}
                onChange={e => setFlightData(d => ({ ...d, to_airport: formatIcaoInput(e.target.value) }))}
                maxLength={4}
              />
            </div>
          </div>
        </div>

        {/* OOOI Section */}
        <div className="fd-oooi-section">
          <div className="fd-paired-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Scheduled Time of<br />Departure</div>
              <div className={getOooIInputClass(true, zuluTimes.scheduled_time_departure)}>
                <input type="text" className="form-input fd fd-time-input"
                  placeholder=""
                  value={zuluTimes.scheduled_time_departure}
                  onChange={e => handleZuluChange('scheduled_time_departure', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('scheduled_time_departure', e)} />
              </div>
            </div>
            <div className="fd-paired-cell fd-paired-cell-empty" aria-hidden="true" />
            <div className="fd-paired-cell">
              <div className="fd-label">Time Off-block</div>
              <div className={getOooIInputClass(true, zuluTimes.time_off_block, false)}>
                <input type="text" className="form-input fd fd-time-input"
                  placeholder=""
                  value={zuluTimes.time_off_block}
                  onChange={e => handleZuluChange('time_off_block', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_off_block', e)} />
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time TakeOff</div>
              <div className={getOooIInputClass(false, zuluTimes.time_takeoff)}>
                <input type="text" className="form-input fd fd-time-input"
                  placeholder=""
                  value={zuluTimes.time_takeoff}
                  onChange={e => handleZuluChange('time_takeoff', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_takeoff', e)}
                  disabled />
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time On-block</div>
              <div className={getOooIInputClass(false, zuluTimes.time_on_block)}>
                <input type="text" className="form-input fd fd-time-input"
                  placeholder=""
                  value={zuluTimes.time_on_block}
                  onChange={e => handleZuluChange('time_on_block', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_on_block', e)}
                  disabled />
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Time Landing</div>
              <div className={getOooIInputClass(false, zuluTimes.time_landing)}>
                <input type="text" className="form-input fd fd-time-input"
                  placeholder=""
                  value={zuluTimes.time_landing}
                  onChange={e => handleZuluChange('time_landing', e.target.value)}
                  onKeyDown={e => handleZuluKeyDown('time_landing', e)}
                  disabled />
              </div>
            </div>
          </div>

          <div className="fd-paired-grid fd-calc-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Block Time</div>
              <div className="fd-time-calc-box fd-time-calc-left">
                {flightData.time_off_block && flightData.time_on_block
                  ? formatMinutesToHourMin(getElapsedMinutes(flightData.time_off_block, flightData.time_on_block))
                  : ''}
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Flight Time</div>
              <div className="fd-time-calc-box fd-time-calc-left">
                {flightData.time_takeoff && flightData.time_landing
                  ? formatMinutesToHourMin(getElapsedMinutes(flightData.time_takeoff, flightData.time_landing))
                  : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Landing Section */}
        <div className="fd-landing-section">
          <div className="fd-paired-grid">
            <div className="fd-paired-cell">
              <div className="fd-label">Full Stop Landing</div>
              <div className="fd-time-calc-box fd-time-calc-left">{flightData.full_stop_landings}</div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Push back this Sector</div>
              <div className="fuel-unit-toggle fuel-v2-unit-toggle fd-pushback-toggle">
                <button type="button" className={`toggle-option ${!flightData.pushback ? 'active' : ''}`}
                  onClick={() => setFlightData(d => ({ ...d, pushback: false }))}>No</button>
                <button type="button" className={`toggle-option ${flightData.pushback ? 'active' : ''}`}
                  onClick={() => setFlightData(d => ({ ...d, pushback: true }))}>Yes</button>
              </div>
            </div>
            <div className="fd-paired-cell">
              <div className="fd-label">Touch & Go</div>
              <div className="fd-time-calc-box fd-time-calc-left">{flightData.touch_and_go}</div>
            </div>
            <div />
          </div>

          <div className="fd-paired-grid fd-single-left">
            <div className="fd-paired-cell">
              <div className="fd-label">Total Landings</div>
              <div className="fd-time-calc-box fd-time-calc-left">{flightData.total_landings}</div>
            </div>
            <div />
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'crew') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />ON GROUND</button>
        <div className="ogm-page-title">Crew Names</div>
        <button className="ogm-save-btn" onClick={handleSaveCrew} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        <div className="crew-card">
          <div className="crew-card-title">Crew Names</div>

          <div className="crew-header-row">
            <span />
            <span className="crew-col-title">Name</span>
            <span className="crew-col-title">Title</span>
            <span />
          </div>

          <div className="crew-list">
            {crew.map((m, i) => (
              <div key={i} className={`crew-row anim-record-enter ${removingCrewIndex === i ? 'anim-record-exit' : ''}`}>
                <span className="crew-member-label">Crew Member {i + 1}</span>
                <input className="form-input crew-name-input" placeholder="Name"
                  value={m.full_name}
                  onChange={e => {
                    const next = [...crew];
                    next[i] = { ...next[i], full_name: e.target.value };
                    setCrew(next);
                  }} />
                <select className="form-select ui-dropdown crew-title-select" value={m.title}
                  onChange={e => {
                    const next = [...crew];
                    next[i] = { ...next[i], title: e.target.value };
                    setCrew(next);
                  }}>
                  {TITLES.map(t => <option key={t}>{t}</option>)}
                </select>
                {i > 0 && crew.length > 1 ? (
                  <button type="button" className="fuel-remove-btn" title="Remove this uplift" onClick={() => removeCrewMember(i)}>✕</button>
                ) : (
                  <span className="crew-remove-spacer" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
        </div>

        <button className={`crew-add-btn anim-add-btn ${animateCrewAddBtn ? 'anim-add-btn-active' : ''}`}
          onClick={handleAddCrewMember}>
          Add Crew Member
        </button>
      </div>
    </div>
  );

  // Update uplift card field with max capacity enforcement for departure fuel
  const updateUpliftField = (index, field, value) => {
    // For departure fuel fields, enforce max capacity
    if (field.startsWith('departure_fuel_')) {
      setFuelUplifts(prev => {
        const newUplifts = [...prev];
        const uplift = { ...newUplifts[index], [field]: value };

        // Calculate total of all departure fuel fields
        const total =
          toFuelNumber(uplift.departure_fuel_left) +
          toFuelNumber(uplift.departure_fuel_center) +
          toFuelNumber(uplift.departure_fuel_right) +
          toFuelNumber(uplift.departure_fuel_other);

        // If over capacity, cap the current field value
        if (total > maxFuelCapacity) {
          const otherFieldsTotal = total - toFuelNumber(value);
          const maxAllowedForThisField = maxFuelCapacity - otherFieldsTotal;
          uplift[field] = maxAllowedForThisField > 0 ? String(Math.max(0, maxAllowedForThisField)) : '0';
        }

        newUplifts[index] = uplift;
        return newUplifts;
      });
    } else {
      setFuelUplifts(prev => prev.map((u, i) =>
        i === index ? { ...u, [field]: value } : u
      ));
    }
  };

  // Add new fuel uplift card
  const handleAddFuelUplift = () => {
    setFuelUplifts(prev => [...prev, createEmptyUplift(prev.length + 1)]);
    setAnimateFuelAddBtn(true);
    setTimeout(() => setAnimateFuelAddBtn(false), 240);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollArea = document.querySelector('.ogm-scroll-area');
        scrollArea?.scrollTo({ top: scrollArea.scrollHeight, behavior: 'smooth' });
      });
    });
  };

  // Remove fuel uplift card
  const handleRemoveFuelUplift = (index) => {
    if (removingFuelIndex !== null) return;
    if (fuelUplifts.length <= 1 || index <= 0) return;
    setRemovingFuelIndex(index);
    setTimeout(() => {
      setFuelUplifts(prev => prev.filter((_, i) => i !== index).map((u, i) => ({ ...u, uplift_number: i + 1 })));
      setRemovingFuelIndex(null);
    }, ROW_EXIT_ANIMATION_MS);
  };

  // Validate numeric input (no negative, only numbers)
  const handleNumericInput = (value, setter) => {
    // Allow empty string or valid positive numbers
    if (value === '' || value === '.' || (/^\d*\.?\d*$/.test(value) && parseFloat(value) >= 0)) {
      setter(value);
    }
  };

  const isFuelDateTimeValid = !!parseFuelDateTimeInput(fuelDateTime || getCurrentDisplayDateTime());
  const isOilDateTimeValid = !!parseFuelDateTimeInput(fuelDateTime || getCurrentDisplayDateTime());
  const hasFuelComplete = hasFuelData && isFuelDateTimeValid;
  const hasOilComplete = isOilDateTimeValid &&
    isOilQuantityValid(oil.eng1_oil_added, oilMaxQt) &&
    isOilQuantityValid(oil.eng2_oil_added, oilMaxQt);
  const hasSecuritySearchComplete = securitySearch.is_na || securitySearch.performed;
  const hasNoBlockingComplaints = openAndOverdueItems.length === 0;
  const canAcceptAircraft = hasFlightData && hasCrew && hasFuelComplete &&
    hasOilComplete && preFlightChecked && hasSecuritySearchComplete && hasNoBlockingComplaints;

  if (view === 'fuel') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />ON GROUND</button>
        <div className="ogm-page-title">Fuel Page</div>
        <button className="ogm-save-btn" onClick={handleSaveFuel} disabled={saving || !hasFuelData || !isFuelDateTimeValid}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area ogm-fuel-page fuel-v2-page">
        <div className="fuel-card fuel-v2-top-card">
          <div className="fuel-v2-grid fuel-v2-grid-top">
            <div className="fuel-v2-field">
              <div className="fd-label">Date/Time</div>
              <input
                className="form-input fuel-date-input"
                type="text"
                value={fuelDateTime}
                readOnly
                placeholder="03.Mar.2026 23:12"
                maxLength={17}
              />
            </div>

            <div className="fuel-v2-field">
              <div className="fd-label">Remaining Fuel</div>
              <div className={`fuel-cell-input fuel-v2-cell ${isFuelValueFilled(fuelDetail.remaining.left) ? 'valid' : 'invalid'}`}>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-input"
                  value={fuelDetail.remaining.left}
                  onChange={e => handleNumericInput(e.target.value, updateRemainingFuelTotal)}
                />
                <span>kg</span>
              </div>
            </div>
          </div>
        </div>

        <div className="fuel-uplifts-section expanded">
          {fuelUplifts.map((uplift, index) => {
            const departureFuelTotal = toFuelNumber(uplift.departure_fuel_left);
            const actualUpliftKg = getActualUpliftKg(index);
            const fuelAfterUpliftKg = getFuelAfterUpliftKg(index);
            const calculatedDifference = isFuelValueFilled(uplift.departure_fuel_left)
              ? Math.abs(fuelAfterUpliftKg - departureFuelTotal)
              : 0;

            return (
              <div key={index} className={`fuel-card fuel-v2-uplift-card anim-record-enter ${removingFuelIndex === index ? 'anim-record-exit' : ''}`}>
                <div className="fuel-card-title">
                  <span className="ogm-icon-placeholder" aria-hidden="true"><img src={fuelUpliftIcon} alt="" className="ogm-icon-img app-icon app-icon-fueluplift" /></span>
                  <span>Fuel Uplift {index + 1}</span>
                  {fuelUplifts.length > 1 && index > 0 && (
                    <button
                      type="button"
                      className="fuel-remove-btn"
                      onClick={() => handleRemoveFuelUplift(index)}
                      title="Remove this uplift"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="fuel-v2-grid">
                  <div className="fuel-v2-field">
                    <div className="fd-label">Fuel Type</div>
                    <div className="fuel-v2-cell fuel-v2-plain-cell fuel-type-shell">
                      <div className="deicing-type-dropdown fuel-type-dropdown" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          className={`deicing-type-trigger fuel-type-trigger ${uplift.fuel_type ? 'has-value' : ''}`}
                          onClick={() => setFuelTypeDropdownOpenIndex(open => (open === index ? null : index))}
                        >
                          <span className="deicing-type-trigger-main">
                            {uplift.fuel_type || ''}
                          </span>
                          <span
                            className={`deicing-type-trigger-arrow ${fuelTypeDropdownOpenIndex === index ? 'open' : ''}`}
                            aria-hidden="true"
                          >
                            ▾
                          </span>
                        </button>
                        <div className={`deicing-type-menu ${fuelTypeDropdownOpenIndex === index ? 'open' : 'closed'}`}>
                          {FUEL_TYPES.map(type => (
                            <button
                              key={type}
                              type="button"
                              className={`deicing-type-option ${uplift.fuel_type === type ? 'active' : ''}`}
                              onClick={() => {
                                updateUpliftField(index, 'fuel_type', type);
                                setFuelTypeDropdownOpenIndex(null);
                              }}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="fuel-v2-field fuel-v2-unit-field">
                    <div className="fuel-unit-toggle fuel-v2-unit-toggle">
                      <button
                        type="button"
                        className={`toggle-option ${(uplift.fuel_unit || 'metric') === 'metric' ? 'active' : ''}`}
                        onClick={() => updateUpliftField(index, 'fuel_unit', 'metric')}
                      >
                        Metric
                      </button>
                      <button
                        type="button"
                        className={`toggle-option ${(uplift.fuel_unit || 'metric') === 'imperial' ? 'active' : ''}`}
                        onClick={() => updateUpliftField(index, 'fuel_unit', 'imperial')}
                      >
                        Imperial
                      </button>
                    </div>
                  </div>
                </div>

                <div className="fuel-divider" />

                <div className="fuel-v2-grid">
                  <div className="fuel-v2-field">
                    <div className="fd-label">Density</div>
                    <div className="fuel-v2-cell fuel-v2-plain-cell fuel-v2-value-cell">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-input"
                        value={uplift.density}
                        onChange={e => handleNumericInput(e.target.value, (val) => updateUpliftField(index, 'density', val))}
                      />
                      <span>kg/l</span>
                    </div>
                  </div>

                  <div className="fuel-v2-field" />

                  <div className="fuel-v2-field">
                    <div className="fd-label">Actual Uplift</div>
                    <div className={`fuel-cell-input fuel-v2-cell ${isFuelValueFilled(uplift.bowser_liters) ? 'valid' : 'invalid'}`}>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-input"
                        value={uplift.bowser_liters}
                        onChange={e => handleNumericInput(e.target.value, (val) => updateUpliftField(index, 'bowser_liters', val))}
                      />
                      <span>{(uplift.fuel_unit || 'metric') === 'imperial' ? 'gal' : 'l'}</span>
                    </div>
                  </div>

                  <div className="fuel-v2-field">
                    <div className="fd-label">Actual Uplift</div>
                    <div className="fuel-total-box fuel-v2-total-box">{formatFuelValue(actualUpliftKg)} <span>kg</span></div>
                  </div>
                </div>

                <div className="fuel-divider" />

                <div className="fuel-v2-grid fuel-v2-grid-summary">
                  <div className="fuel-v2-field" />
                  <div className="fuel-v2-field">
                    <div className="fd-label">Fuel after Uplift</div>
                    <div className="fuel-total-box fuel-v2-total-box">{formatFuelValue(fuelAfterUpliftKg)} <span>kg</span></div>
                  </div>

                  <div className="fuel-v2-field">
                    <div className="fd-label">Calculated Difference</div>
                    <div className="fuel-total-box fuel-v2-total-box">{formatFuelValue(calculatedDifference)} <span>kg</span></div>
                  </div>

                  <div className="fuel-v2-field">
                    <div className="fd-label">Departure Fuel</div>
                    <div className={`fuel-cell-input fuel-v2-cell ${isFuelValueFilled(uplift.departure_fuel_left) ? 'valid' : 'invalid'}`}>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-input"
                        value={uplift.departure_fuel_left}
                        onChange={e => handleNumericInput(e.target.value, (val) => updateDepartureFuelTotal(index, val))}
                      />
                      <span>kg</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="fuel-actions fuel-v2-actions">
            <button type="button" className={`fuel-add-btn fuel-v2-add-btn anim-add-btn ${animateFuelAddBtn ? 'anim-add-btn-active' : ''}`} onClick={handleAddFuelUplift}>
              <span className="ogm-icon-placeholder" aria-hidden="true"><img src={fuelUpliftIcon} alt="" className="ogm-icon-img app-icon app-icon-fueluplift ogm-icon-sm" /></span>
              <span>Add Fuel Uplift</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'oil') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />ON GROUND</button>
        <div className="ogm-page-title">Before Flight Oil Data</div>
        <button className="ogm-save-btn" onClick={handleSaveOil} disabled={saving || !isOilDateTimeValid}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        <div className="oil-card">
          <div className="oil-card-title">
            <img src={oilIcon} alt="" className="oil-card-title-icon app-icon app-icon-oil" />
            <span>Oil Before Flight</span>
          </div>

          <div className="oil-grid">
            <div className="oil-field">
              <div className="oil-label">Date/Time</div>
              <div className={`oil-required-wrap ${isOilDateTimeValid ? 'complete' : 'incomplete'}`}>
                <input
                  className="form-input fuel-date-input oil-date-input"
                  type="text"
                  value={fuelDateTime}
                  readOnly
                  placeholder="03.Mar.2026 23:12"
                  maxLength={17}
                />
              </div>
            </div>

            <div className="oil-field">
              <div className="oil-label">Engine 1</div>
              <div className={`oil-required-wrap ${isOilQuantityValid(oil.eng1_oil_added, oilMaxQt) ? 'complete' : 'incomplete'}`}>
                <div className="oil-suffix-wrap">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max={oilMaxQt}
                    className="form-input oil-qty-input"
                    value={oil.eng1_oil_added}
                    onChange={e => setOil(o => ({ ...o, eng1_oil_added: formatOilQuantityInput(e.target.value, oilMaxQt) }))}
                    placeholder="Type in quantity"
                  />
                  <span className="oil-suffix">qt</span>
                </div>
              </div>
            </div>

            <div className="oil-field">
              <div className="oil-label">Engine 2</div>
              <div className={`oil-required-wrap ${isOilQuantityValid(oil.eng2_oil_added, oilMaxQt) ? 'complete' : 'incomplete'}`}>
                <div className="oil-suffix-wrap">
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max={oilMaxQt}
                    className="form-input oil-qty-input"
                    value={oil.eng2_oil_added}
                    onChange={e => setOil(o => ({ ...o, eng2_oil_added: formatOilQuantityInput(e.target.value, oilMaxQt) }))}
                    placeholder="Type in quantity"
                  />
                  <span className="oil-suffix">qt</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'deicing') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />ON GROUND</button>
        <div className="ogm-page-title">De-Icing/Anti-Icing - Ground Mode</div>
        <button className="ogm-save-btn" onClick={handleSaveDeicing} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="ogm-scroll-area deicing-scroll-area">
        {deicingRecords.map((record, index) => (
          <div className={`deicing-record-card anim-record-enter ${removingDeicingIndex === index ? 'anim-record-exit' : ''}`} key={`deicing-record-${index}`}>
            <div className="deicing-record-title">
              <div className="deicing-record-title-left">
                <img src={deicingIcon} alt="" className="deicing-record-title-icon app-icon app-icon-deicing" />
                <span>Record {index + 1}</span>
              </div>
              {index > 0 ? (
                <button
                  type="button"
                  className="fuel-remove-btn"
                  onClick={() => removeDeicingRecord(index)}
                  title="Remove this record"
                >
                  ✕
                </button>
              ) : (
                <span className="crew-remove-spacer" aria-hidden="true" />
              )}
            </div>
            <div className="deicing-grid">
              <div className="deicing-field deicing-field-wide">
                <label className="deicing-label">Fluid Type</label>
                <div className={`deicing-required-wrap fuel-v2-cell fuel-v2-plain-cell fuel-type-shell ${record.fluid_type ? 'complete' : 'incomplete'}`}>
                  <div className="deicing-type-dropdown fuel-type-dropdown" onClick={e => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`deicing-type-trigger fuel-type-trigger ${record.fluid_type ? 'has-value' : ''}`}
                      onClick={() => setDeicingFluidDropdownOpenIndex(open => (open === index ? null : index))}
                    >
                      <span className="deicing-type-trigger-main">
                        {record.fluid_type || ''}
                      </span>
                      <span
                        className={`deicing-type-trigger-arrow ${deicingFluidDropdownOpenIndex === index ? 'open' : ''}`}
                        aria-hidden="true"
                      >
                        ▾
                      </span>
                    </button>
                    <div className={`deicing-type-menu ${deicingFluidDropdownOpenIndex === index ? 'open' : 'closed'}`}>
                      <button
                        type="button"
                        className={`deicing-type-option ${!record.fluid_type ? 'active' : ''}`}
                        onClick={() => {
                          updateDeicingRecord(index, { fluid_type: '' });
                          setDeicingFluidDropdownOpenIndex(null);
                        }}
                      >
                        None
                      </button>
                      {DEICING_FLUID_TYPES.map(type => (
                        <button
                          key={type}
                          type="button"
                          className={`deicing-type-option ${record.fluid_type === type ? 'active' : ''}`}
                          onClick={() => {
                            updateDeicingRecord(index, { fluid_type: type });
                            setDeicingFluidDropdownOpenIndex(null);
                          }}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Mixture</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input deicing-input"
                    value={record.mixture}
                    onChange={e => {
                      const nextValue = handleDeicingNumericInput(e.target.value);
                      if (nextValue !== null) updateDeicingRecord(index, { mixture: nextValue });
                    }}
                  />
                  <span className="deicing-suffix">%</span>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Quantity</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input deicing-input"
                    value={record.quantity}
                    onChange={e => {
                      const nextValue = handleDeicingNumericInput(e.target.value);
                      if (nextValue !== null) updateDeicingRecord(index, { quantity: nextValue });
                    }}
                  />
                  <span className="deicing-suffix">l</span>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Start UTC</label>
                <input
                  type="text"
                  className="form-input deicing-input"
                  value={record.start_utc}
                  onChange={e => handleDeicingZuluChange(index, e.target.value)}
                  onKeyDown={e => handleDeicingZuluKeyDown(index, e)}
                />
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Holdover Min</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-input deicing-input"
                    value={record.holdover_min}
                    onChange={e => updateDeicingRecord(index, { holdover_min: handleDeicingMinutesInput(e.target.value) })}
                  />
                  <span className="deicing-suffix">min</span>
                </div>
              </div>

              <div className="deicing-field">
                <label className="deicing-label">Holdover Max</label>
                <div className="deicing-suffix-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-input deicing-input"
                    value={record.holdover_max}
                    onChange={e => updateDeicingRecord(index, { holdover_max: handleDeicingMinutesInput(e.target.value) })}
                  />
                  <span className="deicing-suffix">min</span>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="fuel-actions">
          <button type="button" className={`fuel-add-btn anim-add-btn ${animateDeicingAddBtn ? 'anim-add-btn-active' : ''}`} onClick={addDeicingRecord}>Add New Record</button>
        </div>
      </div>
    </div>
  );

  if (view === 'createComplaint') return renderCreateComplaintPage();

  if (view === 'dispatchComplaint') return renderDispatchComplaintPage();

  if (view === 'melBrowser') return renderMelBrowserPage();

  if (view === 'complaintDetail') return (
    <>
      {complaintTransitionUnderlay === 'createComplaint' ? renderCreateComplaintPage('ogm-container ogm-subview-overlay ogm-subview-underlay') : null}
      {renderComplaintDetailPage()}
      {voidComplaintModalOpen ? renderVoidComplaintModal() : null}
    </>
  );

  if (view === 'delay') return (
    <div className={viewContainerClass}>
      <div className="ogm-sub-header">
        <button className="ogm-back-btn" onClick={navigateBackToMain}><img src={arrowLeftIcon} alt="" className="ogm-back-arrow app-icon app-icon-arrow-sm-left" />ON GROUND</button>
        <div className="ogm-page-title">Delay</div>
        <button className="ogm-save-btn" onClick={async () => {
          if (!delay.delay_code || !isDelayMinutesValid) return;
          setSaving(true);
          try {
            await addDelay(flightId, {
              delay_code: delay.delay_code,
              delay_description: delay.delay_description || null,
              delay_minutes: Number(delay.delay_minutes)
            });
            setDelay(prev => ({ ...prev, delay_description: '', delay_minutes: '' }));
            await loadFlight();
            navigateBackToMain();
          } catch (err) {
            console.error(err);
          }
          setSaving(false);
        }} disabled={saving || !delay.delay_code || !isDelayMinutesValid}>
          {saving ? 'Saving...' : 'Add Delay'}
        </button>
      </div>
      <div className="ogm-scroll-area">
        <div className="delay-entry-card">
          <div className="delay-selected-code">
            {selectedDelayOption ? `${selectedDelayOption.code} (${selectedDelayOption.reason}) ${selectedDelayOption.description}` : ''}
          </div>

          <div className="delay-entry-field">
            <label className="delay-entry-label">Minutes</label>
            <div className={`delay-minutes-wrap ${isDelayMinutesValid ? 'complete' : 'incomplete'}`}>
              <input
                type="text"
                inputMode="numeric"
                className="form-input"
                value={delay.delay_minutes}
                onChange={e => setDelay(d => ({ ...d, delay_minutes: handleDelayMinutesInput(e.target.value) }))}
              />
            </div>
          </div>

          <div className="delay-entry-field">
            <label className="delay-entry-label">Description</label>
            <input
              className="form-input"
              value={delay.delay_description}
              onChange={e => setDelay(d => ({ ...d, delay_description: e.target.value }))}
            />
          </div>
        </div>

        {/* List existing delays */}
        {flight?.delays?.length > 0 && (
          <div className="delay-list">
            <h4 className="ogm-section-label">Added Delays</h4>
            {flight.delays.map(d => (
              <div key={d.id} className={`delay-item anim-record-enter ${removingDelayId === d.id ? 'anim-record-exit' : ''}`}>
                <div className="delay-info">
                  <span className="delay-code">{d.delay_code}</span>
                  <span className="delay-desc">{d.delay_description}</span>
                  <span className="delay-mins">{d.delay_minutes} min</span>
                </div>
                <button className="delay-remove-btn" disabled={removingDelayId !== null} onClick={() => handleRemoveDelay(d.id)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (view === 'aircraftStatus') return (
    <div className={viewContainerClass}>
      <AircraftStatusReport
        aircraftId={aircraft?.id}
        embeddedMode
        showFlyButton={false}
        onBack={navigateBackFromAircraftStatus}
        backLabel={aircraftStatusReturnView === 'createComplaint' ? 'CREATE COMPLAINT' : 'ON GROUND'}
        rootTitle="Aircraft Status"
        initialSection="main"
        initialComplaintId={aircraftStatusInitialComplaint?.id || null}
        initialComplaint={aircraftStatusInitialComplaint}
        initialDetailReturnView={aircraftStatusReturnView === 'createComplaint' || aircraftStatusReturnView === 'onGround' ? 'external' : 'main'}
        detailBackLabel={aircraftStatusReturnView === 'createComplaint' ? 'CREATE COMPLAINT' : aircraftStatusReturnView === 'onGround' ? 'ON GROUND' : 'OVERVIEW'}
        refreshSignal={refreshSignal}
      />
    </div>
  );

  if (view === 'closedComplaints') return (
    <div className={viewContainerClass}>
      <AircraftStatusReport
        aircraftId={aircraft?.id}
        embeddedMode
        showFlyButton={false}
        onBack={navigateBackToMain}
        backLabel="ON GROUND"
        rootTitle="Closed Complaints"
        initialSection="closed"
        refreshSignal={refreshSignal}
      />
    </div>
  );

  if (view === 'cabinItems') return (
    <div className={viewContainerClass}>
      <AircraftStatusReport
        aircraftId={aircraft?.id}
        embeddedMode
        showFlyButton={false}
        onBack={navigateBackToMain}
        backLabel="ON GROUND"
        rootTitle="Cabin Items"
        initialSection="cabin"
        refreshSignal={refreshSignal}
      />
    </div>
  );

  /* ── MAIN VIEW ──────────────────────────────────────────── */

  // Calculate stats for "For Your Information" section
  const activeStatusComplaints = complaints.filter(c => String(c.status || '').toUpperCase() !== 'CLOSED');
  const pilotItems = activeStatusComplaints.filter(c => c.type === 'Pilot').length;
  const maintenanceItems = complaints.filter(c => c.type === 'Maintenance' || c.category === 'Maintenance').length;
  const cabinItems = complaints.filter(c => c.type === 'Cabin').length;
  const totalStatus = activeStatusComplaints.length;
  const recentlyClosed = complaints.filter(c => String(c.status || '').toUpperCase() === 'CLOSED').length;
  const cabinOpen = complaints.filter(c => c.type === 'Cabin' && String(c.status || '').toUpperCase() === 'OPEN').length;
  const cabinAccepted = complaints.filter(c => c.type === 'Cabin' && String(c.status || '').toUpperCase() === 'ACCEPTED').length;
  const cabinDeferred = complaints.filter(c => c.type === 'Cabin' && String(c.status || '').toUpperCase() === 'DEFERRED').length;
  const cabinClosed = complaints.filter(c => c.type === 'Cabin' && String(c.status || '').toUpperCase() === 'CLOSED').length;

  return (
    <div className={viewContainerClass}>
      <div className="ogm-header">
        <div className="ogm-pill">On Ground</div>
        <button className="ogm-accept-btn" onClick={() => { if (canAcceptAircraft) setAcceptModalOpen(true); }} disabled={saving || !canAcceptAircraft}>Accept Aircraft</button>
      </div>

      <div className="ogm-scroll-area">
        <h3 className="ogm-section-label">Mandatory Tasks</h3>

        <TaskRow label="Flight Data" isComplete={hasFlightData}
          statusText={!hasFlightData ? 'Incomplete Flight Data' : ''}
          onClick={() => openSubView('flightData')}
          iconSrc={flightDataIcon}
          iconClassName="app-icon-flightdata ogm-icon-lg" />

        <TaskRow label="Crew Members Names" isComplete={hasCrew}
          statusText={!hasCrew ? 'Incomplete Crew Members Names' : ''}
          onClick={() => openSubView('crew')}
          iconSrc={crewIcon}
          iconClassName="app-icon-crew ogm-icon-lg" />

        {/* Open and Overdue Items */}
        <div className={`ogm-item ${openAndOverdueItems.length ? 'incomplete ogm-mcc-section-alert' : 'complete'} ogm-mcc-section`}>
          <div className="ogm-mcc-header">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={openComplaintsIcon} alt="" className="ogm-icon-img app-icon app-icon-opencomplaints ogm-icon-sm" /></span>
            <span>{`Open and Overdue Items (${openAndOverdueItems.length})`}</span>
          </div>
          <div className="ogm-mcc-content">
            {openAndOverdueItems.map(c => (
              <div key={c.id} className="mcc-complaint-block">
                <button type="button" className={`mcc-complaint-item ${getComplaintLineClass(c)}`} onClick={() => openOnGroundComplaintDetail(c.id)}>
                  <div className="mcc-complaint-main">
                    <div className="mcc-complaint-top">
                      <span className="mcc-complaint-wo">{c.work_order_number || `WO.${c.id}`}</span>
                    <span className={`mcc-complaint-badge ${getComplaintStatusBadgeClass(c)}`}>{getComplaintStatusBadgeLabel(c)}</span>
                      <span className="mcc-complaint-badge mcc-complaint-badge-muted">{c.type || c.category || 'Pilot'}</span>
                    </div>
                    <div className="mcc-complaint-desc">{c.defect_description}</div>
                    <div className="mcc-complaint-meta">
                      <div className="mcc-complaint-meta-item">
                        <span className="mcc-complaint-meta-label">Opened</span>
                        <span className="mcc-complaint-meta-value">{formatDisplayDate(c.opened_at)}</span>
                      </div>
                      <div className="mcc-complaint-meta-item">
                        <span className="mcc-complaint-meta-label">Due at</span>
                        <span className="mcc-complaint-meta-value">
                          {c.due_at ? `${formatDisplayDate(c.due_at)}${c.due_hours ? ` (-${c.due_hours} H)` : ''}` : '---'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Chevron />
                </button>
                <div className="mcc-complaint-actions">
                  <button
                    type="button"
                    className="ogm-create-complaint-btn complaint-action-btn"
                    onClick={() => {
                      selectComplaintForAction(c);
                      setVoidComplaintModalOpen(true);
                    }}
                  >
                    Void Complaint
                  </button>
                  <button
                    type="button"
                    className="ogm-create-complaint-btn complaint-action-btn"
                    onClick={() => openDispatchComplaintPage(c)}
                  >
                    Dispatch Complaint
                  </button>
                </div>
              </div>
            ))}
            <div className="ogm-mcc-actions">
              <button className="ogm-create-complaint-btn" onClick={() => openSubView('createComplaint')}>
                + Create Complaint
              </button>
            </div>
          </div>
        </div>

        <TaskRow label="Fuel Uplift" isComplete={hasFuelComplete}
          statusText={!hasFuelComplete ? 'Incomplete Fuel Uplift Data' : ''}
          onClick={() => openSubView('fuel')}
          iconSrc={fuelUpliftIcon}
          iconClassName="app-icon-fueluplift ogm-icon-sm" />

        <TaskRow label="Before Flight Oil Data" isComplete={hasOilComplete}
          statusText={!hasOilComplete ? 'Incomplete Pre-Flight Oil Data' : ''}
          onClick={() => openSubView('oil')}
          iconSrc={oilIcon}
          iconClassName="app-icon-oil ogm-icon-lg" />

        {/* Pre-Flight Check */}
        <div
          className={`ogm-item ${preFlightChecked ? 'complete' : 'incomplete'} ogm-checkable-row`}
          onClick={() => setPreFlightChecked(value => !value)}
        >
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={preflightIcon} alt="" className="ogm-icon-img app-icon app-icon-preflight" /></span>
            <span>Pre-Flight Check</span>
          </div>
          <div className="ogm-right">
            <span className="ogm-status-text">{preFlightChecked ? 'Pre-Flight Check completed' : 'Incomplete Pre-Flight Check'}</span>
            <div className={`ogm-check-toggle ${preFlightChecked ? 'is-checked' : ''}`} aria-hidden="true">
              <span className="ogm-check-mark">✓</span>
            </div>
          </div>
        </div>

        {/* Security Search */}
        <div className="ogm-item complete ogm-static-row ogm-security-row">
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={securityIcon} alt="" className="ogm-icon-img app-icon app-icon-security" /></span>
            <span>Security Search</span>
          </div>
          <div className="ogm-right ogm-security-right">
            {securityTimeDisplay ? <span className="ogm-security-time">{securityTimeDisplay}</span> : null}
            <div className="ogm-security-buttons">
              <button
                type="button"
                className={`ogm-security-btn ogm-security-btn-na ${securitySearch.is_na ? 'active' : ''}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  const newState = { performed: false, is_na: true, performed_at: null };
                  setSecuritySearch(newState);
                  try {
                    await saveSecuritySearch(flightId, newState);
                  } catch (err) {
                    console.error(err);
                  }
                }}
              >N/A</button>
              <button
                type="button"
                className={`ogm-security-btn ogm-security-btn-yes ${securitySearch.performed ? 'active' : ''}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  const nowIso = new Date().toISOString();
                  const newState = { performed: true, is_na: false, performed_at: nowIso };
                  setSecuritySearch(newState);
                  setSecurityUtcNow(new Date());
                  try {
                    await saveSecuritySearch(flightId, newState);
                  } catch (err) {
                    console.error(err);
                  }
                }}
              >Yes</button>
            </div>
          </div>
        </div>

        <h3 className="ogm-section-label">Optional Tasks</h3>

        <TaskRow label="De-Icing/Anti-Icing" neutral onClick={() => openSubView('deicing')} iconSrc={deicingIcon} iconClassName="app-icon-deicing" />

        <h3 className="ogm-section-label">For Your Information</h3>

        {/* Aircraft Status */}
        <div className="ogm-item ogm-info-row" onClick={() => openSubView('aircraftStatus')}>
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={aircraftStatusIcon} alt="" className="ogm-icon-img app-icon app-icon-aircraftstatus ogm-icon-lg" /></span>
            <span>Aircraft Status</span>
          </div>
          <div className="ogm-right ogm-info-stats">
            <div className="ogm-stat">
              <span className="ogm-stat-label">Pilot Items</span>
              <span className="ogm-stat-value">{pilotItems}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Maintenance Items</span>
              <span className="ogm-stat-value">{maintenanceItems}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Cabin Items</span>
              <span className="ogm-stat-value">{cabinItems}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Status</span>
              <span className="ogm-stat-value">{totalStatus}</span>
            </div>
            <Chevron />
          </div>
        </div>

        {/* Closed Complaints */}
        <div className="ogm-item ogm-info-row" onClick={() => openSubView('closedComplaints')}>
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={openComplaintsIcon} alt="" className="ogm-icon-img app-icon app-icon-opencomplaints" /></span>
            <span>Closed Complaints</span>
          </div>
          <div className="ogm-right ogm-info-stats">
            <div className="ogm-stat">
              <span className="ogm-stat-label">Recently Closed</span>
              <span className="ogm-stat-value">{recentlyClosed}</span>
            </div>
            <Chevron />
          </div>
        </div>

        {/* Cabin Items */}
        <div className="ogm-item ogm-info-row" onClick={() => openSubView('cabinItems')}>
          <div className="ogm-left">
            <span className="ogm-icon-placeholder" aria-hidden="true"><img src={cabinItemsIcon} alt="" className="ogm-icon-img app-icon app-icon-cabinitems" /></span>
            <span>Cabin Items</span>
          </div>
          <div className="ogm-right ogm-info-stats">
            <div className="ogm-stat">
              <span className="ogm-stat-label">Open</span>
              <span className="ogm-stat-value">{cabinOpen}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Accepted</span>
              <span className="ogm-stat-value">{cabinAccepted}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Deferred</span>
              <span className="ogm-stat-value">{cabinDeferred}</span>
            </div>
            <div className="ogm-stat">
              <span className="ogm-stat-label">Recently Closed</span>
              <span className="ogm-stat-value">{cabinClosed}</span>
            </div>
            <Chevron />
          </div>
        </div>

        <button className="btn btn-danger" onClick={handleCancel}>Cancel Flight</button>
      </div>

      {acceptModalOpen && (
        <CommanderConfirmationModal
          title="Commander's Acceptance"
          actionLabel="Accept Aircraft"
          pending={saving}
          onClose={() => { if (!saving) setAcceptModalOpen(false); }}
          onConfirm={handleAccept}
          confirmationText="Check here to confirm that the Pre-Flight Inspection has been performed. Fuel/Oil, ATL state and security search are all satisfactory before flight. Aircraft Status Report (ASR) is checked and there are no overdue and due maintenance items for the intended flight. Applicable MEL items (if any) have been reviewed; limitations, restrictions, operational procedures are applied as prescribed in the MEL."
        />
      )}
      {voidComplaintModalOpen ? renderVoidComplaintModal() : null}
      {cancelModalOpen && (
        <CancelFlightModal
          pending={saving}
          onClose={() => { if (!saving) setCancelModalOpen(false); }}
          onConfirm={confirmCancelFlight}
        />
      )}
    </div>
  );
};

export default OnGroundMode;
